import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useStateContext } from "contexts/ContextProvider";
import {
  Box,
  useTheme,
  Typography,
  Button,
  Divider,
  useMediaQuery,
  Stack,
  ListItemIcon,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Grid2";
import { useParams } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import Fab from "@mui/material/Fab";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { OVERLAY_NAMES } from "constants/Overlays";
import { countryOptions } from "constants/countryData";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import LocalPhoneRoundedIcon from "@mui/icons-material/LocalPhoneRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import {
  formatDate,
  formatSelectedMonths,
  getStatusColor,
  numericComparator,
} from "helpers/ServiceHelpers";
import CustomTab from "components/CustomTab";
import api, { isAbortedRequestError } from "services/api";
import AppRegistrationRoundedIcon from "@mui/icons-material/AppRegistrationRounded";
import {
  DataGrid,
  gridClasses,
  GridFooter,
  GridFooterContainer,
  GridToolbarContainer,
  useGridApiRef,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import CustomDateSelector from "components/CustomDateSelector";
import { FILTER_SELECTOR_WIDTH } from "constants/filterSelectors";
import { a11yProps, CustomTabPanel } from "components/CustomTabPanel";
import { Card } from "components/Card";
import { Card2 } from "components/Card2";
import ColumnFilter from "components/ColumnFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import useDataGridScrollRestore from "hooks/useDataGridScrollRestore";
import ClearIcon from "@mui/icons-material/ClearRounded";
import ColumnDateFilter from "components/ColumnDateFilter";
import TaskItem from "components/TaskItem";
import { TaskBoard, buildTaskQueryParams } from "../../workspace/tasks/Tasks";
import { DragDropContext, Draggable } from "@hello-pangea/dnd";
import { useAuth } from "contexts/AuthContext";
import { usePageTitle } from "hooks/usePageTitle";
import { HtmlTooltip } from "components/HeatMap";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import RequestorGroupsTab from "scenes/connections/clients/RequestorGroupsTab";
import { CONST } from "constants/CONST";
import { NAVBAR_HEIGHT } from "constants/layout";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";
import useEntityUpdates from "hooks/useEntityUpdates";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { useWebSocket } from "contexts/WebSocketContext";
import History from "../../services/History";
import { parsePhoneNumber } from "libphonenumber-js";

// Helper: format a phone number based on country code (national format)
const formatPhoneNumber = (raw, countryCode) => {
  if (!raw) return "";
  try {
    const phoneNumber = parsePhoneNumber(raw, countryCode);
    if (phoneNumber && phoneNumber.isValid()) {
      let formatted = phoneNumber.formatNational();
      const cc = String(phoneNumber.countryCallingCode || "");
      // Strip leading trunk prefix (0) for non-US numbers
      if (cc && cc !== "1" && formatted.startsWith("0")) {
        formatted = formatted.slice(1);
      }
      // Include country code prefix for non-US numbers
      if (cc && cc !== "1") {
        formatted = `+${cc} ${formatted}`;
      }
      return formatted;
    }
  } catch {
    // fall through
  }
  return raw;
};

const formatRelativeTime = (dateValue) => {
  if (!dateValue) return "N/A";
  const parsed = dayjs(dateValue);
  if (!parsed.isValid()) return "N/A";

  const now = dayjs();
  const diffDays = now.diff(parsed, "day");
  if (diffDays < 1) return "today";
  if (diffDays < 30) {
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  }

  const diffMonths = now.diff(parsed, "month");
  if (diffMonths < 12) {
    return `${diffMonths} month${diffMonths === 1 ? "" : "s"} ago`;
  }

  const diffYears = now.diff(parsed, "year");
  return `${diffYears} year${diffYears === 1 ? "" : "s"} ago`;
};

export const ColorButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

const ClientProfile = () => {
  const theme = useTheme();

  const [tabValue, setTabValue] = React.useState(0);
  const [mobileTabValue, setMobileTabValue] = React.useState(0);
  const [pageTitle, setPageTitle] = useState("Client");
  const [clientHistories, setClientHistories] = useState([]);
  const [clientNotes, setClientNotes] = useState([]);
  usePageTitle(pageTitle);

  const [taskStatuses, setTaskStatuses] = useState([]);
  const [tasksForService, setTasksForService] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);

  // Inline task creation state
  const [creatingTaskForStatus, setCreatingTaskForStatus] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState("");

  const { currentUser } = useAuth();
  const { socket } = useWebSocket();

  const handleTabsChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleMobileTabsChange = (event, newValue) => {
    setMobileTabValue(newValue);
    const sectionRef = mobileSectionRefs[newValue];
    if (sectionRef?.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const { id } = useParams();

  const navigate = useNavigate();

  // Refs for mobile scroll-to-view and scroll spy
  const requestorsRef = useRef(null);
  const groupsRef = useRef(null);
  const contactPersonsRef = useRef(null);
  const tasksRef = useRef(null);
  const projectsRef = useRef(null);
  const ratesRef = useRef(null);
  const notesRef = useRef(null);
  const historyRef = useRef(null);
  const mobileScrollRef = useRef(null);
  const mobileTabRef = useRef(0);
  const mobileSectionRefs = useMemo(
    () => [
      requestorsRef,
      groupsRef,
      contactPersonsRef,
      tasksRef,
      projectsRef,
      ratesRef,
      notesRef,
      historyRef,
    ],
    [
      requestorsRef,
      groupsRef,
      contactPersonsRef,
      tasksRef,
      projectsRef,
      ratesRef,
      notesRef,
      historyRef,
    ],
  );

  const [isLoading, setIsLoading] = useState(true);

  const { refreshPage, triggerPageRefresh, showSnackbar } = useStateContext();
  const { setOverlayName, setOverlayData } = useStateContext();
  const { openDialog, handleErrorDialog, closeDialog } = useStateContext();

  const fetchClientNotes = useCallback(async () => {
    if (!id) return;

    const notesResponse = await api.get(`/clients/${id}/notes`);
    setClientNotes(notesResponse.data || []);
  }, [id]);

  const handleAddClientNote = useCallback(() => {
    setOverlayData({
      Action: OVERLAY_NAMES.CLIENT_NOTE_ACTION_NEW,
      ClientID: id,
      onSaved: fetchClientNotes,
    });
    setOverlayName(OVERLAY_NAMES.CLIENT_NOTE_NEW_EDIT);
  }, [fetchClientNotes, id, setOverlayData, setOverlayName]);

  const [clientNoteAnchorEl, setClientNoteAnchorEl] = useState(null);
  const [selectedClientNote, setSelectedClientNote] = useState(null);

  const handleClientNoteMenuOpen = useCallback((event, note) => {
    setClientNoteAnchorEl(event.currentTarget);
    setSelectedClientNote(note);
  }, []);

  const handleClientNoteMenuClose = useCallback(() => {
    setClientNoteAnchorEl(null);
    setSelectedClientNote(null);
  }, []);

  const handleEditClientNote = useCallback(() => {
    if (!selectedClientNote) return;

    setOverlayData({
      Action: OVERLAY_NAMES.CLIENT_NOTE_ACTION_EDIT,
      ClientID: id,
      NoteID: selectedClientNote.id,
      Body: selectedClientNote.Body || "",
      onSaved: fetchClientNotes,
    });
    setOverlayName(OVERLAY_NAMES.CLIENT_NOTE_NEW_EDIT);
    handleClientNoteMenuClose();
  }, [
    fetchClientNotes,
    handleClientNoteMenuClose,
    id,
    selectedClientNote,
    setOverlayData,
    setOverlayName,
  ]);

  const handleDeleteClientNote = useCallback(() => {
    if (!selectedClientNote) return;

    openDialog({
      theme,
      title: "Delete Confirmation",
      message: "Are you sure you'd like to delete this note?",
      primaryAction: "Delete",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        try {
          await api.delete(`/clients/notes/${selectedClientNote.id}`);
          closeDialog();
          handleClientNoteMenuClose();
          await fetchClientNotes();
          showSnackbar("Note deleted", theme, "success");
        } catch (error) {
          const message =
            error.response?.data?.error ||
            error.response?.data?.message ||
            "Failed to delete note.";
          handleErrorDialog(message, theme);
        }
      },
      secondaryOnClick: () => {
        closeDialog();
      },
    });
  }, [
    closeDialog,
    fetchClientNotes,
    handleClientNoteMenuClose,
    handleErrorDialog,
    openDialog,
    selectedClientNote,
    showSnackbar,
    theme,
  ]);

  const [clientData, setClientData] = useState(false);

  const [listofClientsTypes, setListOfClientsTypes] = useState([]);
  const [listofClientServiceTypes, setListOfClientServiceTypes] = useState([]);
  const [listOfPaymentMethods, setListOfPaymentMethods] = useState([]);
  // amazonq-ignore-next-line
  const [listofContractStatuses, setListOfContractStatuses] = useState([]);
  const [listofProjects, setListOfProjects] = useState([]);
  const [listOfRequestorSummaries, setListOfRequestorSummaries] = useState([]);
  const [listOfContactPersons, setListOfContactPersons] = useState([]);
  const [listOfLanguages, setListOfLanguages] = useState([]);

  // Preserve scroll positions across refetches (saves bounce to the top)
  const requestorsApiRef = useGridApiRef();
  const { capture: captureRequestorsScroll, restore: restoreRequestorsScroll } =
    useDataGridScrollRestore(requestorsApiRef);
  const contactPersonsApiRef = useGridApiRef();
  const {
    capture: captureContactPersonsScroll,
    restore: restoreContactPersonsScroll,
  } = useDataGridScrollRestore(contactPersonsApiRef);
  const historyApiRef = useGridApiRef();
  const { capture: captureHistoryScroll, restore: restoreHistoryScroll } =
    useDataGridScrollRestore(historyApiRef);

  // Global filter state management
  const [openFilter, setOpenFilter] = useState(null);
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });

  const handleSortModelChange = (newSortModel) => {
    //setSortModel(newSortModel);
  };

  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
  };

  const [clientRates, setClientRates] = useState();

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Year selector data
  const currentYear = dayjs().year();

  const [yearOptions, setYearOptions] = useState([String(currentYear)]);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonthsByYear, setAvailableMonthsByYear] = useState({});
  const [dateOptionsLoaded, setDateOptionsLoaded] = useState(false);

  const [selectedYear, setSelectedYear] = useState([String(currentYear)]);
  const [tempSelectedYear, setTempSelectedYear] = useState([
    String(currentYear),
  ]);

  useEffect(() => {
    const fetchTaskEnums = async () => {
      try {
        const [statusesRes] = await Promise.all([
          api.get("/tasks/enums/statuses"),
        ]);
        setTaskStatuses(statusesRes.data || []);
      } catch (error) {
        console.error("Error fetching task enums:", error);
      }
    };

    fetchTaskEnums();
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchTasksForService = async () => {
      if (!id) return;
      setTasksLoading(true);
      try {
        const params = buildTaskQueryParams(
          {
            entityServiceTypes: ["Client"],
            showAllCompleted: true,
          },
          currentUser?.id,
        );
        params.append("entityId", id);

        const response = await api.get(`/tasks?${params.toString()}`, {
          signal: controller.signal,
        });
        setTasksForService(response.data || []);
      } catch (error) {
        if (isAbortedRequestError(error)) {
          return;
        }
        console.error("Error fetching tasks for service:", error);
        handleErrorDialog("Failed to load tasks", theme);
      } finally {
        if (!controller.signal.aborted) {
          setTasksLoading(false);
        }
      }
    };

    fetchTasksForService();

    return () => {
      controller.abort();
    };
  }, [currentUser?.id, handleErrorDialog, id, refreshPage, theme]);

  // Listen for task updates linked to this client
  useEffect(() => {
    if (!socket || !id) return;

    const handleTaskUpdate = async (data) => {
      // Check if task is linked to this client
      if (data.entityType === "Client" && data.entityID === parseInt(id)) {
        // Refetch tasks for this client
        try {
          const params = buildTaskQueryParams(
            {
              entityServiceTypes: ["Client"],
              showAllCompleted: true,
            },
            currentUser?.id,
          );
          params.append("entityId", id);

          const response = await api.get(`/tasks?${params.toString()}`);
          setTasksForService(response.data || []);

          // Show snackbar notification
          const userName = data.updatedBy?.userName || "Unknown User";
          const message = `Task ${data.changeAction} by ${userName}`;
          showSnackbar(message, theme, "info");
        } catch (error) {
          if (!isAbortedRequestError(error)) {
            console.error("Error refreshing tasks:", error);
          }
        }
      }
    };

    socket.on("task:updated", handleTaskUpdate);
    return () => socket.off("task:updated", handleTaskUpdate);
  }, [socket, id, currentUser?.id, showSnackbar, theme]);

  const currentMonth = dayjs().format("MMM");

  const [selectedMonths, setSelectedMonths] = useState([String(currentMonth)]);
  const [tempSelectedMonths, setTempSelectedMonths] = useState([
    String(currentMonth),
  ]);

  useEffect(() => {
    if (!id) return;

    setDateOptionsLoaded(false);

    const fetchDateOptions = async () => {
      try {
        const response = await api.get(`/services/byclient/${id}/date-options`);
        const payload = response?.data || {};

        const allYears = Array.isArray(payload.years)
          ? payload.years.map((year) => String(year))
          : [];
        const activeYears = Array.isArray(payload.availableYears)
          ? payload.availableYears.map((year) => String(year))
          : [];
        const monthsByYear = Object.entries(payload.monthsByYear || {}).reduce(
          (acc, [year, monthsForYear]) => {
            acc[String(year)] = Array.isArray(monthsForYear)
              ? monthsForYear
              : [];
            return acc;
          },
          {},
        );

        const resolvedYearOptions =
          allYears.length > 0
            ? allYears
            : activeYears.length > 0
              ? activeYears
              : [String(currentYear)];

        setYearOptions(resolvedYearOptions);
        setAvailableYears(activeYears);
        setAvailableMonthsByYear(monthsByYear);

        const preferredYear =
          activeYears.find((year) => year === String(currentYear)) ||
          activeYears[activeYears.length - 1] ||
          "";
        const monthsForPreferredYear = monthsByYear[preferredYear] || [];
        const preferredMonth =
          monthsForPreferredYear.find((month) => month === currentMonth) ||
          monthsForPreferredYear[0] ||
          "";

        const nextSelectedYear = preferredYear ? [preferredYear] : [];
        const nextSelectedMonths = preferredMonth ? [preferredMonth] : [];

        setSelectedYear(nextSelectedYear);
        setTempSelectedYear(nextSelectedYear);
        setSelectedMonths(nextSelectedMonths);
        setTempSelectedMonths(nextSelectedMonths);
      } catch (error) {
        console.error("Error fetching client date options:", error);
        setYearOptions([String(currentYear)]);
        setAvailableYears([]);
        setAvailableMonthsByYear({});
        setSelectedYear([String(currentYear)]);
        setTempSelectedYear([String(currentYear)]);
        setSelectedMonths([String(currentMonth)]);
        setTempSelectedMonths([String(currentMonth)]);
      } finally {
        setDateOptionsLoaded(true);
      }
    };

    fetchDateOptions();
  }, [currentMonth, currentYear, id, refreshPage]);

  // Handle date selection save (months and years combined)
  const handleDateSelectionSave = (newSelection) => {
    const updatedSelection = {
      months: newSelection.months,
      years: newSelection.years.slice(-1), // Single year
    };
    setSelectedMonths(updatedSelection.months);
    setTempSelectedMonths(updatedSelection.months);
    setSelectedYear(updatedSelection.years);
    setTempSelectedYear(updatedSelection.years);
  };

  const [filterRowIds, setFilterRowIds] = useState([]);
  const prevFilteredRowIdsRef = useRef([]);

  // Helper function to generate localStorage key for column filters
  const getFilterStorageKey = useCallback((fieldName) => {
    // Convert field name to uppercase and create key
    // e.g., "Client" -> "CLIENT_PROFILE_FILTER_CLIENT"
    const upperFieldName = fieldName.toUpperCase();
    return `CLIENT_PROFILE_FILTER_${upperFieldName}`;
  }, []);

  // Helper function to save a column filter to localStorage
  const saveColumnFilter = useCallback(
    (fieldName, values) => {
      const key = getFilterStorageKey(fieldName);
      if (values && values.length > 0) {
        localStorage.setItem(key, JSON.stringify(values));
      } else {
        // Remove from localStorage if no values selected
        localStorage.removeItem(key);
      }
    },
    [getFilterStorageKey],
  );

  // Helper function to load a column filter from localStorage
  const loadColumnFilter = useCallback(
    (fieldName) => {
      const key = getFilterStorageKey(fieldName);
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : [];
    },
    [getFilterStorageKey],
  );

  // Helper function to load all saved column filters
  const loadAllColumnFilters = useCallback(() => {
    const savedFilters = {};
    // Get all column field names
    const fields = [
      "ProjectNumber",
      "Date",
      "Client",
      "Requestor",
      "InterpretationStatus",
      "AccountCharge",
      "Details",
      "Linguist",
      "ServiceCategory",
      "TotalCost",
      "AdditionalCost",
      "LinguistPay",
      "Profit",
      "Margin",
      "Language",
      "TargetLanguage",
      "CreatedBy",
      "CheckedBy",
      "CheckedOn",
    ];

    fields.forEach((field) => {
      const values = loadColumnFilter(field);
      if (values.length > 0) {
        savedFilters[field] = values;
      }
    });

    return savedFilters;
  }, [loadColumnFilter]);

  // Helper function to clear all column filters from localStorage
  const clearAllColumnFilters = useCallback(() => {
    const fields = [
      "ProjectNumber",
      "Date",
      "Client",
      "Requestor",
      "InterpretationStatus",
      "AccountCharge",
      "Details",
      "Linguist",
      "ServiceCategory",
      "TotalCost",
      "AdditionalCost",
      "LinguistPay",
      "Profit",
      "Margin",
      "Language",
      "TargetLanguage",
      "CreatedBy",
      "CheckedBy",
      "CheckedOn",
    ];

    fields.forEach((field) => {
      localStorage.removeItem(getFilterStorageKey(field));
    });
  }, [getFilterStorageKey]);

  const customFooter = () => {
    return (
      <GridFooterContainer>
        {/* Add what you want here */}

        <Box
          sx={{
            p: "1rem",
            display: "flex",
            flexWrap: "wrap", // wrap to new line if too long
            gap: 2, // spacing between items
            alignItems: "center",
          }}
        >
          <IconButton
            onClick={handleToggleShowTotals}
            size="small"
            sx={{
              color: theme.palette.primary.main,
              "&:hover": { color: theme.palette.primary.buttonHoverSelected },
            }}
          >
            <SwapHorizRoundedIcon fontSize="small" />
          </IconButton>

          {(showTotals ? listOfTotals : listOfServiceStatuses).map((item) => {
            return (
              <Box key={`${item}-${item.name}`}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ flexWrap: "wrap", justifyContent: "center" }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {item.name}:
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: theme?.palette?.secondary?.[400],
                      fontWeight: 600,
                    }}
                  >
                    {showTotals
                      ? `$${Number(item.total).toLocaleString()}`
                      : item.count}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Box>

        <GridFooter
          sx={{
            border: "none", // remove double border
          }}
        />
      </GridFooterContainer>
    );
  };

  const fetchProjectsData = useCallback(
    (months = selectedMonths, year = selectedYear) => {
      setIsLoading(true);
      captureHistoryScroll();

      // Ensure we have valid parameters
      if (!months || months.length === 0) {
        months = [dayjs().format("MMM")];
        // Update both states to reflect the default value
        setSelectedMonths(months);
      }

      if (!year || year.length === 0) {
        year = [String(currentYear)];
        // Update both states to reflect the default value
        setSelectedYear(year);
      }

      // Convert months to a comma-separated string if it's an array
      const monthsParam = Array.isArray(months) ? months.join(",") : months;

      api
        .get(`/services/byclient/${id}`, {
          params: {
            months: monthsParam,
            year: year[0], // Since year is single-select, we only need the first (and only) value
            // amazonq-ignore-next-line
          },
        })
        .then((response) => {
          if (response !== null) {
            //REVERSE LISTS
            const services = response.data;

            services.reverse();
            setListOfProjects(services);
            restoreHistoryScroll(services);
            setIsLoading(false);
          } else {
            setListOfProjects([]);
            setIsLoading(false);
          }
        })
        .catch((error) => {
          const message =
            error.response?.data?.message ||
            "Unable to connect to the server. Please try again later.";

          handleErrorDialog(message, theme);
          setIsLoading(false);
        });
    },
    [
      currentYear,
      handleErrorDialog,
      id,
      selectedMonths,
      selectedYear,
      theme,
      captureHistoryScroll,
      restoreHistoryScroll,
    ],
  );

  useEffect(() => {
    if (!id || !dateOptionsLoaded) return;

    if (!selectedYear?.length || !selectedMonths?.length) {
      setListOfProjects([]);
      setIsLoading(false);
      return;
    }

    fetchProjectsData(selectedMonths, selectedYear);
  }, [
    dateOptionsLoaded,
    fetchProjectsData,
    id,
    selectedMonths,
    selectedYear,
  ]);

  useEffect(() => {
    const fetchData = () => {
      captureRequestorsScroll();
      captureContactPersonsScroll();
      api
        .all([
          api.get(`/clients/${id}`),
          api.get(`/clients/${id}/requestorsSummary`),
          api.get("/contactPersons", { params: { clientId: id } }),
          api.get("/clients/clientTypes"),
          api.get("/clients/contractStatuses"),
          api.get("/clients/clientServiceTypes"),
          api.get("/clients/paymentMethods"),
          api.get(`/clients/clientrates/${id}`),
          api.get(`/languages/`),
          api.get(`/auditLogs/entity/Client/${id}`, { params: { limit: 1000 } }),
        ])
        .then(
          api.spread(
            (
              clientResponse,
              requestorSummaryResponse,
              contactPersonsResponse,
              typesResponse,
              statusesResponse,
              serviceTypesResponse,
              paymentMethodsResponse,
              ratesResponse,
              languagesResponse,
              auditLogsResponse,
            ) => {
              if (clientResponse) {
                setClientData(clientResponse.data);
                setPageTitle("Client: " + clientResponse.data.Account);
              }
              if (requestorSummaryResponse) {
                // Server returns an object keyed by requestor ID, not an array
                const rawData = requestorSummaryResponse.data || {};
                const requestorArray = Array.isArray(rawData)
                  ? rawData
                  : Object.values(rawData);
                // Normalize casing: server returns servicesCount/lastServiceDate,
                // but columns reference ServicesCount/LastServiceDate
                const normalized = requestorArray.map((r) => ({
                  ...r,
                  ServicesCount: r.ServicesCount ?? r.servicesCount ?? 0,
                  LastServiceDate:
                    r.LastServiceDate ?? r.lastServiceDate ?? null,
                }));
                setListOfRequestorSummaries(normalized);
                restoreRequestorsScroll(normalized);
              }
              if (contactPersonsResponse) {
                setListOfContactPersons(contactPersonsResponse.data || []);
                restoreContactPersonsScroll(contactPersonsResponse.data || []);
              }
              if (typesResponse) {
                setListOfClientsTypes(typesResponse.data);
              }
              if (statusesResponse) {
                setListOfContractStatuses(statusesResponse.data);
              }
              if (serviceTypesResponse) {
                setListOfClientServiceTypes(serviceTypesResponse.data);
              }
              if (paymentMethodsResponse) {
                setListOfPaymentMethods(paymentMethodsResponse.data);
              }
              if (ratesResponse) {
                setClientRates(ratesResponse.data);
              }
              if (languagesResponse) {
                const tempLanguages = languagesResponse.data.filter(
                  (language) => language.Name !== "ALL",
                );
                setListOfLanguages(tempLanguages);
              }
              if (auditLogsResponse) {
                setClientHistories(auditLogsResponse.data?.auditLogs || []);
              }
              setIsLoading(false);
            },
          ),
        )
        .catch((error) => {
          const message =
            error.response?.data?.error ||
            error.response?.data?.message ||
            "Unable to connect to the server. Please try again later.";
          if (error.response?.status === 404) {
            navigate("/404", { replace: true });
          } else {
            handleErrorDialog(message, theme);
          }
          setIsLoading(false);
        });
    };

    fetchData();
  }, [
    handleErrorDialog,
    id,
    navigate,
    refreshPage,
    theme,
    captureRequestorsScroll,
    restoreRequestorsScroll,
    captureContactPersonsScroll,
    restoreContactPersonsScroll,
  ]);

  useEffect(() => {
    fetchClientNotes();
  }, [fetchClientNotes, refreshPage]);

  useEffect(() => {
    if (!id) {
      return;
    }
  }, [id]);

  const dateComparator = (v1, v2) => {
    if (v1 === "N/A") return 1;
    if (v2 === "N/A") return -1;
    return dayjs(v1, "M/D/YYYY").valueOf() - dayjs(v2, "M/D/YYYY").valueOf();
  };

  const getColumns = () => [
    {
      field: "ProjectNumber",
      headerName: "Number",
      minWidth: 120,
      valueGetter: (_, params) => {
        const data = params.ProjectNumber;
        const value = data ? data : "N/A";
        return value;
      },
      flex: 1.2,
      renderHeader: () => (
        <DataGridHeader
          headerName="Number"
          children={
            <ColumnFilter
              field="ProjectNumber"
              data={filterData[0] || []}
              selectedValues={columnFilters["ProjectNumber"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("ProjectNumber", values)
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "ProjectNumber")?.sort || null
              }
            />
          }
        />
      ),
    },
    {
      field: "Date",
      headerName: "Date",
      valueGetter: (_, params) => {
        const data = params.Date;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 120,
      flex: 1.2,
      renderHeader: () => (
        <DataGridHeader
          headerName="Date"
          children={
            <ColumnDateFilter
              field="Date"
              data={filterData[1] || []}
              valueGetter={(_, params) => {
                const data = params.Date;
                return data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
              }}
              selectedValues={columnFilters["Date"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("Date", values)
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "Date")?.sort || null
              }
            />
          }
        />
      ),
    },
    {
      field: "Client",
      headerName: "Client",
      valueGetter: (_, params) => {
        const data = params.Client;
        const value = data ? data.Account : "N/A";
        return value;
      },
      minWidth: 180,
      flex: 1.8,
      renderHeader: () => (
        <DataGridHeader
          headerName="Client"
          children={
            <ColumnFilter
              field="Client"
              data={filterData[2] || []}
              valueGetter={(_, params) => {
                const data = params.Client;
                return data ? data.Account : "N/A";
              }}
              selectedValues={columnFilters["Client"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("Client", values)
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "Client")?.sort || null
              }
            />
          }
        />
      ),
    },
    {
      field: "Requestor",
      headerName: "Requestor",
      valueGetter: (_, params) => {
        const data = params.Requestor;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: () => (
        <DataGridHeader
          headerName="Requestor"
          children={
            <ColumnFilter
              field="Requestor"
              data={filterData[3] || []}
              valueGetter={(_, params) => {
                const data = params.Requestor;
                return data ? data.Name : "N/A";
              }}
              selectedValues={columnFilters["Requestor"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("Requestor", values)
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "Requestor")?.sort || null
              }
            />
          }
        />
      ),
    },
    {
      field: "InterpretationStatus",
      headerName: "Status",
      valueGetter: (_, params) => {
        const value =
          params.InterpretationStatus ||
          params.TranslationStatus ||
          params.OtherServiceStatus ||
          "N/A";
        return value;
      },
      minWidth: 120,
      flex: 1.2,
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <HtmlTooltip
            title={
              <Typography sx={{ whiteSpace: "pre-wrap" }}>
                {params.value}
              </Typography>
            }
          >
            <Box
              sx={{
                borderRadius: "8px",
                px: "0.5rem",
                backgroundColor: getStatusColor(theme, params.value),
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: theme.palette.background.default,
                }}
              >
                {params.value}
              </Typography>
            </Box>
          </HtmlTooltip>
        </Box>
      ),
      renderHeader: () => (
        <DataGridHeader
          headerName="Status"
          children={
            <ColumnFilter
              field="InterpretationStatus"
              data={filterData[4] || []}
              selectedValues={columnFilters["InterpretationStatus"] || []}
              onFilterChange={(values) =>
                setColumnFilters((prev) => ({
                  ...prev,
                  InterpretationStatus: values,
                }))
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "InterpretationStatus")
                  ?.sort || null
              }
            />
          }
        />
      ),
    },
    {
      field: "AccountCharge",
      headerName: "Account Charge",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.AccountCharge;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value =
            parseFloat(data) === 0
              ? "N/A"
              : `$${parseFloat(data).toLocaleString()}`;
        }

        return value;
      },
      minWidth: 165,
      flex: 1.65,
      renderHeader: () => (
        <DataGridHeader
          headerName="Account Charge"
          children={
            <ColumnFilter
              field="AccountCharge"
              data={filterData[5] || []}
              selectedValues={columnFilters["AccountCharge"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("AccountCharge", values)
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "AccountCharge")?.sort || null
              }
            />
          }
        />
      ),
    },
    {
      field: "Details",
      headerName: "Details",
      valueGetter: (_, params) => {
        const data = params.Details;
        const value = data ? data : "N/A";
        return value;
      },
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      minWidth: 450,
      flex: 4.5,
      renderHeader: () => (
        <DataGridHeader
          headerName="Details"
          children={
            <ColumnFilter
              field="Details"
              data={filterData[6] || []}
              selectedValues={columnFilters["Details"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("Details", values)
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "Details")?.sort || null
              }
            />
          }
        />
      ),
    },
    {
      field: "Linguist",
      headerName: "Linguist",
      valueGetter: (_, params) => {
        const data = params.Linguist;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: () => (
        <DataGridHeader
          headerName="Linguist"
          children={
            <ColumnFilter
              field="Linguist"
              data={filterData[7] || []}
              valueGetter={(_, params) => {
                const data = params.Linguist;
                return data ? data.Name : "N/A";
              }}
              selectedValues={columnFilters["Linguist"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("Linguist", values)
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "Linguist")?.sort || null
              }
            />
          }
        />
      ),
    },
    {
      field: "ServiceCategory",
      headerName: "Category",
      valueGetter: (_, params) => {
        const data = params.ServiceCategory;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: () => (
        <DataGridHeader
          headerName="Category"
          children={
            <ColumnFilter
              field="ServiceCategory"
              data={filterData[8] || []}
              selectedValues={columnFilters["ServiceCategory"] || []}
              onFilterChange={(values) =>
                setColumnFilters((prev) => ({
                  ...prev,
                  ServiceCategory: values,
                }))
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "ServiceCategory")?.sort ||
                null
              }
            />
          }
        />
      ),
    },
    {
      field: "TotalCost",
      headerName: "Total Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.TotalCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 130,
      flex: 1.3,
      renderHeader: () => (
        <DataGridHeader
          headerName="Total Cost"
          children={
            <ColumnFilter
              field="TotalCost"
              data={filterData[9] || []}
              selectedValues={columnFilters["TotalCost"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("TotalCost", values)
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "TotalCost")?.sort || null
              }
            />
          }
        />
      ),
    },
    {
      field: "AdditionalCost",
      headerName: "Additional Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.AdditionalCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 165,
      flex: 1.65,
      renderHeader: () => (
        <DataGridHeader
          headerName="Additional Cost"
          children={
            <ColumnFilter
              field="AdditionalCost"
              data={filterData[10] || []}
              selectedValues={columnFilters["AdditionalCost"] || []}
              onFilterChange={(values) =>
                setColumnFilters((prev) => ({
                  ...prev,
                  AdditionalCost: values,
                }))
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "AdditionalCost")?.sort ||
                null
              }
            />
          }
        />
      ),
    },
    {
      field: "LinguistPay",
      headerName: "Linguist Pay",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.LinguistPay;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 145,
      flex: 1.45,
      renderHeader: () => (
        <DataGridHeader
          headerName="Linguist Pay"
          children={
            <ColumnFilter
              field="LinguistPay"
              data={filterData[11] || []}
              selectedValues={columnFilters["LinguistPay"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("LinguistPay", values)
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "LinguistPay")?.sort || null
              }
            />
          }
        />
      ),
    },
    {
      field: "Profit",
      headerName: "Profit",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.Profit;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 95,
      flex: 0.95,
      renderHeader: () => (
        <DataGridHeader
          headerName="Profit"
          children={
            <ColumnFilter
              field="Profit"
              data={filterData[12] || []}
              selectedValues={columnFilters["Profit"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("Profit", values)
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "Profit")?.sort || null
              }
            />
          }
        />
      ),
    },
    {
      field: "Margin",
      headerName: "Margin",
      valueGetter: (_, params) => {
        const data = params.Margin;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          const percentage = data * 1;
          if (percentage === 0) {
            value = "0%";
          } else if (percentage === 100) {
            value = "100%";
          } else {
            value = `${percentage.toFixed(2)}%`;
          }
        }

        return value;
      },
      minWidth: 105,
      flex: 1.05,
      renderHeader: () => (
        <DataGridHeader
          headerName="Margin"
          children={
            <ColumnFilter
              field="Margin"
              data={filterData[13] || []}
              selectedValues={columnFilters["Margin"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("Margin", values)
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "Margin")?.sort || null
              }
            />
          }
        />
      ),
    },
    {
      field: "Language",
      headerName: "Source Language",
      valueGetter: (_, params) => {
        const data = params.Language;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 175,
      flex: 1.75,
      renderHeader: () => (
        <DataGridHeader
          headerName="Source Language"
          children={
            <ColumnFilter
              field="Language"
              data={filterData[14] || []}
              valueGetter={(_, params) => {
                const data = params.Language;
                const value = data ? data.Name : "N/A";
                return value;
              }}
              selectedValues={columnFilters["Language"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("Language", values)
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "Language")?.sort || null
              }
            />
          }
        />
      ),
    },
    {
      field: "TargetLanguage",
      headerName: "Target Languages",
      valueGetter: (_, params) => {
        const languages = params.ServiceTargetLanguages;
        if (!languages || languages.length === 0) return "N/A";
        return languages
          .map((item) =>
            item.Language && item.Language.Name ? item.Language.Name : "",
          )
          .filter((name) => name)
          .join(" | ");
      },
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      minWidth: 300,
      flex: 3.0,
      renderHeader: () => (
        <DataGridHeader
          headerName="Target Languages"
          children={
            <ColumnFilter
              field="TargetLanguage"
              data={filterData[15] || []}
              valueGetter={(_, params) => {
                const languages = params.ServiceTargetLanguages;
                if (!languages || languages.length === 0) return "N/A";
                return languages
                  .map((item) =>
                    item.Language && item.Language.Name
                      ? item.Language.Name
                      : "",
                  )
                  .filter((name) => name)
                  .join(" | ");
              }}
              selectedValues={columnFilters["TargetLanguage"] || []}
              onFilterChange={(values) =>
                setColumnFilters((prev) => ({
                  ...prev,
                  TargetLanguage: values,
                }))
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "TargetLanguage")?.sort ||
                null
              }
            />
          }
        />
      ),
    },
    {
      field: "CreatedBy",
      headerName: "Created By",
      valueGetter: (_, params) => {
        const data = params.Creator;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 130,
      flex: 1.3,
      renderHeader: () => (
        <DataGridHeader
          headerName="Created By"
          children={
            <ColumnFilter
              field="CreatedBy"
              data={filterData[16] || []}
              valueGetter={(_, params) => {
                const data = params.Creator;
                const value = data ? data.Name : "N/A";
                return value;
              }}
              selectedValues={columnFilters["CreatedBy"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("CreatedBy", values)
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "CreatedBy")?.sort || null
              }
            />
          }
        />
      ),
    },
    {
      field: "CheckedBy",
      headerName: "Checked By",
      valueGetter: (_, params) => {
        const data = params.Checker;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 135,
      flex: 1.35,
      renderHeader: () => (
        <DataGridHeader
          headerName="Checked By"
          children={
            <ColumnFilter
              field="CheckedBy"
              data={filterData[17] || []}
              valueGetter={(_, params) => {
                const data = params.Checker;
                const value = data ? data.Name : "N/A";
                return value;
              }}
              selectedValues={columnFilters["CheckedBy"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("CheckedBy", values)
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "CheckedBy")?.sort || null
              }
            />
          }
        />
      ),
    },
    {
      field: "CheckedOn",
      headerName: "Checked On",
      valueGetter: (_, params) => {
        const data = params.CheckingDate;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 135,
      flex: 1.35,
      renderHeader: () => (
        <DataGridHeader
          headerName="Checked On"
          children={
            <ColumnDateFilter
              field="CheckedOn"
              data={filterData[18] || []}
              valueGetter={(_, params) => {
                const data = params.CheckingDate;
                return data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
              }}
              selectedValues={columnFilters["CheckedOn"] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage("CheckedOn", values)
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === "CheckedOn")?.sort || null
              }
            />
          }
        />
      ),
    },
  ];

  const columns = getColumns();

  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
    setColumnFilters: setRawColumnFilters,
  } = useColumnFilters(listofProjects || [], columns);

  // Load saved column filters on mount and apply them
  useEffect(() => {
    if (listofProjects && listofProjects.length > 0) {
      const savedFilters = loadAllColumnFilters();
      setRawColumnFilters(savedFilters);
    }
  }, [listofProjects, loadAllColumnFilters, setRawColumnFilters]);

  // Wrapped filter change handler that saves to localStorage
  const handleColumnFilterChangeWithStorage = (field, values) => {
    handleColumnFilterChange(field, values);
    saveColumnFilter(field, values);
  };

  // Wrapped clear all filters that also clears localStorage
  const handleClearAllFilters = () => {
    clearAllFilters();
    clearAllColumnFilters();
    // Clear quick filter
    setFilterModel({ items: [] });
  };

  // Check if quick filter has values
  const hasQuickFilter = filterModel?.quickFilterValues?.length > 0;

  // Combined check for any active filters
  const hasAnyActiveFilters = hasActiveFilters || hasQuickFilter;

  const setColumnFilters = (filterFn) => {
    const newFilters =
      typeof filterFn === "function" ? filterFn(columnFilters) : filterFn;
    Object.entries(newFilters).forEach(([field, values]) => {
      handleColumnFilterChange(field, values);
    });
  };

  // Filter columns configuration for cross-filtering
  const filterColumns = useMemo(
    () => [
      {
        field: "ProjectNumber",
        valueGetter: (row) => row.ProjectNumber || "N/A",
      },
      {
        field: "Date",
        valueGetter: (row) =>
          row.Date ? dayjs.tz(row.Date).format("M/D/YYYY") : "N/A",
      },
      {
        field: "Client",
        valueGetter: (row) => (row.Client ? row.Client.Account : "N/A"),
      },
      {
        field: "Requestor",
        valueGetter: (row) => (row.Requestor ? row.Requestor.Name : "N/A"),
      },
      {
        field: "InterpretationStatus",
        valueGetter: (row) =>
          row.InterpretationStatus ||
          row.TranslationStatus ||
          row.OtherServiceStatus ||
          "N/A",
      },
      {
        field: "AccountCharge",
        valueGetter: (row) => {
          const data = row.AccountCharge;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0
              ? "N/A"
              : `$${parseFloat(data).toLocaleString()}`;
          }
          return "N/A";
        },
      },
      { field: "Details", valueGetter: (row) => row.Details || "N/A" },
      {
        field: "Linguist",
        valueGetter: (row) => (row.Linguist ? row.Linguist.Name : "N/A"),
      },
      {
        field: "ServiceCategory",
        valueGetter: (row) => row.ServiceCategory || "N/A",
      },
      {
        field: "TotalCost",
        valueGetter: (row) => {
          const data = row.TotalCost;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "AdditionalCost",
        valueGetter: (row) => {
          const data = row.AdditionalCost;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "LinguistPay",
        valueGetter: (row) => {
          const data = row.LinguistPay;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "Profit",
        valueGetter: (row) => {
          const data = row.Profit;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "Margin",
        valueGetter: (row) => {
          const data = row.Margin;
          if (data !== undefined && data !== null) {
            const percentage = data * 1;
            if (percentage === 0) return "0%";
            if (percentage === 100) return "100%";
            return `${percentage.toFixed(2)}%`;
          }
          return "N/A";
        },
      },
      {
        field: "Language",
        valueGetter: (row) => (row.Language ? row.Language.Name : "N/A"),
      },
      {
        field: "TargetLanguage",
        valueGetter: (row) => {
          const languages = row.ServiceTargetLanguages;
          if (!languages || languages.length === 0) return "N/A";
          return languages
            .map((item) =>
              item.Language && item.Language.Name ? item.Language.Name : "",
            )
            .filter((name) => name)
            .join(" | ");
        },
      },
      {
        field: "CreatedBy",
        valueGetter: (row) => (row.Creator ? row.Creator.Name : "N/A"),
      },
      {
        field: "CheckedBy",
        valueGetter: (row) => (row.Checker ? row.Checker.Name : "N/A"),
      },
      {
        field: "CheckedOn",
        valueGetter: (row) =>
          row.CheckingDate
            ? dayjs.tz(row.CheckingDate).format("M/D/YYYY")
            : "N/A",
      },
    ],
    [],
  );

  // Cross-filtering data calculation
  const filterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      if (otherFilters.length === 0) {
        return listofProjects || [];
      }

      return (listofProjects || []).filter((row) => {
        return otherFilters.every(([field, selectedValues]) => {
          if (!selectedValues || selectedValues.length === 0) return true;

          const filterColumn = filterColumns.find((col) => col.field === field);
          if (!filterColumn) return true;

          const value = filterColumn.valueGetter(row);
          return selectedValues.includes(value);
        });
      });
    });
  }, [columnFilters, listofProjects, filterColumns]);

  // Calculate totals after columnFilteredData is available
  const filteredRowIdsSet = new Set((filterRowIds || []).map(String));
  const includeAll = filteredRowIdsSet.size === 0;

  const groupTasksByStatus = (taskList) => {
    const grouped = {};
    taskStatuses.forEach((status) => {
      grouped[status] = [];
    });

    taskList.forEach((task) => {
      if (!grouped[task.Status]) {
        grouped[task.Status] = [];
      }
      grouped[task.Status].push(task);
    });

    return grouped;
  };

  const flattenTasksByStatus = (groupedTasks) => {
    const ordered = [];

    taskStatuses.forEach((status) => {
      if (groupedTasks[status]) {
        ordered.push(...groupedTasks[status]);
      }
    });

    Object.keys(groupedTasks).forEach((status) => {
      if (!taskStatuses.includes(status)) {
        ordered.push(...groupedTasks[status]);
      }
    });

    return ordered;
  };

  const handleTaskDragEnd = async (result) => {
    const { destination, draggableId } = result;

    if (!destination) {
      return;
    }

    const newStatus = destination.droppableId;
    const taskId = draggableId.replace("task-", "");

    setTasksForService((prevTasks) => {
      const taskToMove = prevTasks.find(
        (task) => task.id.toString() === taskId,
      );
      if (!taskToMove) return prevTasks;

      const withoutTask = prevTasks.filter(
        (task) => task.id.toString() !== taskId,
      );
      const tasksByStatus = groupTasksByStatus(withoutTask);

      const updatedTask = { ...taskToMove, Status: newStatus };
      const destinationList = tasksByStatus[newStatus] || [];
      const insertAt = Math.min(destination.index, destinationList.length);
      destinationList.splice(insertAt, 0, updatedTask);
      tasksByStatus[newStatus] = destinationList;

      return flattenTasksByStatus(tasksByStatus);
    });

    try {
      await api.patch(`/tasks/updateStatus/${taskId}`, {
        Status: newStatus,
      });

      // Emit socket event for real-time updates
      if (socket) {
        const taskToUpdate = tasksForService.find(
          (t) => t.id.toString() === taskId,
        );
        if (taskToUpdate) {
          socket.emit("task:saved", {
            modelType: "Task",
            recordId: parseInt(taskId),
            entityType: taskToUpdate.EntityType,
            entityID: taskToUpdate.EntityID,
            serviceType: taskToUpdate.ServiceType,
            changeAction: "Updated",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }
      }
    } catch (error) {
      console.error("Error updating task status:", error);
      handleErrorDialog("Failed to update task status", theme);

      const params = buildTaskQueryParams(
        {
          entityServiceTypes: ["Interpretation"],
          showAllCompleted: true,
        },
        currentUser?.id,
      );
      params.append("entityId", id);
      const response = await api.get(`/tasks?${params.toString()}`);
      setTasksForService(response.data || []);
    }
  };

  // Handle inline task creation
  const handleCreateInlineTask = async (status) => {
    if (!newTaskTitle.trim()) return;

    try {
      const response = await api.post("/tasks/create", {
        Title: newTaskTitle,
        Status: status,
        Priority: "Normal",
        CreatedByUserID: currentUser.id,
        AssignedToUserID: currentUser.id,
        EntityType: "Client",
        EntityID: id,
      });

      setNewTaskTitle("");
      setCreatingTaskForStatus(null);

      const createdTask = response.data;
      setTasksForService((prevTasks) => {
        const tasksByStatus = groupTasksByStatus(prevTasks);
        const statusTasks = tasksByStatus[status] || [];
        tasksByStatus[status] = [...statusTasks, createdTask];
        return flattenTasksByStatus(tasksByStatus);
      });
    } catch (error) {
      console.error("Error creating task:", error);
      handleErrorDialog("Failed to create task", theme);
    }
  };

  const renderTaskCard = (task, index) => (
    <Draggable
      draggableId={`task-${task.id}`}
      index={index}
      key={task.id}
      disableInteractiveElementBlocking
    >
      {(provided, snapshot) => (
        <TaskItem
          key={task.id}
          task={task}
          onClick={() => {
            setOverlayData({ Task: task });
            setOverlayName(OVERLAY_NAMES.TASK_PROFILE);
          }}
          provided={provided}
          snapshot={snapshot}
        />
      )}
    </Draggable>
  );

  let totalAccountCharge = 0;
  let totalTotalCost = 0;
  let totalProfit = 0;

  columnFilteredData &&
    columnFilteredData.forEach((service) => {
      const include = includeAll || filteredRowIdsSet.has(String(service.id));
      if (!include) return;

      totalAccountCharge += Number(service.AccountCharge);
      totalTotalCost += Number(service.TotalCost);
      totalProfit += Number(service.Profit);
    });

  const listOfTotals = [
    { name: "Total Account Charge", total: totalAccountCharge },
    { name: "Total Total Cost", total: totalTotalCost },
    { name: "Total Profit", total: totalProfit },
  ];

  // Count statuses for Members and implement footer toggle
  let completedCount = 0;
  let confirmedCount = 0;
  let requestedCount = 0;
  let cancelledCount = 0;

  columnFilteredData &&
    columnFilteredData.forEach((service) => {
      const include = includeAll || filteredRowIdsSet.has(String(service.id));
      if (!include) return;

      const status =
        service.InterpretationStatus ||
        service.TranslationStatus ||
        service.OtherServiceStatus ||
        null;
      if (status === "Completed") completedCount++;
      else if (status === "Confirmed") confirmedCount++;
      else if (status === "Requested") requestedCount++;
      else if (status === "Cancelled" || service.Cancelled) cancelledCount++;
    });

  const listOfServiceStatuses = [
    { name: "Completed", count: completedCount },
    { name: "Confirmed", count: confirmedCount },
    { name: "Requested", count: requestedCount },
    { name: "Cancelled", count: cancelledCount },
  ];

  const getInitialShowTotals = () => {
    const saved = localStorage.getItem(CONST.CLIENT_PROFILE_SHOW_TOTALS_KEY);
    if (saved !== null) return JSON.parse(saved);
    return currentUser?.role !== "Admin";
  };
  const [showTotals, setShowTotals] = useState(getInitialShowTotals);
  const handleToggleShowTotals = () => {
    const next = !showTotals;
    setShowTotals(next);
    localStorage.setItem(
      CONST.CLIENT_PROFILE_SHOW_TOTALS_KEY,
      JSON.stringify(next),
    );
  };

  const [requestorContextMenu, setRequestorContextMenu] = useState(null);
  const [selectedRequestor, setSelectedRequestor] = useState(null);

  const handleRequestorGridContextMenu = (event) => {
    event.preventDefault();

    const requestorId = Number(event.currentTarget.getAttribute("data-id"));
    if (!requestorId) return;

    const requestor = (listOfRequestorSummaries || []).find(
      (item) => Number(item.id) === requestorId,
    );
    if (!requestor) return;

    setRequestorContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
    setSelectedRequestor(requestor);
  };

  const handleRequestorMenuClose = () => {
    setRequestorContextMenu(null);
    setSelectedRequestor(null);
  };

  const selectedRequestorServicesCount = Number(
    selectedRequestor?.ServicesCount || 0,
  );
  const canDeleteSelectedRequestor = selectedRequestorServicesCount === 0;
  const selectedRequestorPhones = selectedRequestor?.PhoneNumbers || [];
  const selectedRequestorPrimary = selectedRequestorPhones.find((p) => p.IsPrimary) || selectedRequestorPhones[0];
  const requestorCallingCode = selectedRequestorPrimary?.CountryCode || "1";
  const requestorCountryCode =
    countryOptions.find((c) => String(c.callingCode) === String(requestorCallingCode))?.code || "US";
  const selectedRequestorRawPhone = String(
    selectedRequestorPrimary?.Number || selectedRequestor?.PhoneNumber || "",
  ).trim();
  const selectedRequestorPhone = selectedRequestorRawPhone
    ? formatPhoneNumber(selectedRequestorRawPhone, requestorCountryCode)
    : "";
  const selectedRequestorEmail = String(selectedRequestor?.Email || "").trim();
  const canCallSelectedRequestor = Boolean(selectedRequestorPhone);
  const canEmailSelectedRequestor = Boolean(selectedRequestorEmail);

  const callSelectedRequestor = () => {
    if (!canCallSelectedRequestor) return;

    const dialablePhone = selectedRequestorPhone.replace(/[^\d+]/g, "");
    if (!dialablePhone) return;

    window.location.href = `tel:${dialablePhone}`;
    handleRequestorMenuClose();
  };

  const emailSelectedRequestor = () => {
    if (!canEmailSelectedRequestor) return;

    window.location.href = `mailto:${encodeURIComponent(selectedRequestorEmail)}`;
    handleRequestorMenuClose();
  };

  const openMergeRequestorOverlay = useCallback(() => {
    if (!selectedRequestor?.id) {
      showSnackbar("Please select a requestor first.", theme, "error");
      return;
    }

    const requestors = Array.isArray(listOfRequestorSummaries)
      ? listOfRequestorSummaries
      : [];

    if (requestors.length < 2) {
      showSnackbar(
        "At least two requestors are required to merge.",
        theme,
        "error",
      );
      return;
    }

    setOverlayData({
      ClientID: id,
      SelectedRequestor: selectedRequestor,
      Requestors: requestors,
    });
    setOverlayName(OVERLAY_NAMES.REQUESTOR_MERGE);
    handleRequestorMenuClose();
  }, [
    id,
    listOfRequestorSummaries,
    selectedRequestor,
    setOverlayData,
    setOverlayName,
    showSnackbar,
    theme,
  ]);

  // Contact Person context menu state and handlers
  const [contactPersonContextMenu, setContactPersonContextMenu] =
    useState(null);
  const [selectedContactPerson, setSelectedContactPerson] = useState(null);

  const handleContactPersonGridContextMenu = (event) => {
    event.preventDefault();

    const contactPersonId = Number(event.currentTarget.getAttribute("data-id"));
    if (!contactPersonId) return;

    const contactPerson = (listOfContactPersons || []).find(
      (item) => Number(item.id) === contactPersonId,
    );
    if (!contactPerson) return;

    setContactPersonContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
    setSelectedContactPerson(contactPerson);
  };

  const handleContactPersonMenuClose = () => {
    setContactPersonContextMenu(null);
    setSelectedContactPerson(null);
  };

  const selectedContactPersonPhones = selectedContactPerson?.PhoneNumbers || [];
  const selectedContactPersonPrimary = selectedContactPersonPhones.find((p) => p.IsPrimary) || selectedContactPersonPhones[0];
  const selectedContactPersonPhone = String(
    selectedContactPersonPrimary?.Number || selectedContactPerson?.PhoneNumber || "",
  ).trim();
  const selectedContactPersonEmail = String(
    selectedContactPerson?.Email || "",
  ).trim();
  const canCallSelectedContactPerson = Boolean(selectedContactPersonPhone);
  const canEmailSelectedContactPerson = Boolean(selectedContactPersonEmail);

  const callSelectedContactPerson = () => {
    if (!canCallSelectedContactPerson) return;

    const dialablePhone = selectedContactPersonPhone.replace(/[^\d+]/g, "");
    if (!dialablePhone) return;

    window.location.href = `tel:${dialablePhone}`;
    handleContactPersonMenuClose();
  };

  const emailSelectedContactPerson = () => {
    if (!canEmailSelectedContactPerson) return;

    window.location.href = `mailto:${encodeURIComponent(selectedContactPersonEmail)}`;
    handleContactPersonMenuClose();
  };

  const openMergeContactPersonOverlay = useCallback(() => {
    if (!selectedContactPerson?.id) {
      showSnackbar("Please select a contact person first.", theme, "error");
      return;
    }

    const contactPersons = Array.isArray(listOfContactPersons)
      ? listOfContactPersons
      : [];

    if (contactPersons.length < 2) {
      showSnackbar(
        "At least two contact persons are required to merge.",
        theme,
        "error",
      );
      return;
    }

    setOverlayData({
      ClientID: id,
      SelectedContactPerson: selectedContactPerson,
      ContactPersons: contactPersons,
    });
    setOverlayName(OVERLAY_NAMES.CONTACT_PERSON_MERGE);
    handleContactPersonMenuClose();
  }, [
    id,
    listOfContactPersons,
    selectedContactPerson,
    setOverlayData,
    setOverlayName,
    showSnackbar,
    theme,
  ]);

  const deleteContactPerson = () => {
    api
      .delete(`/contactPersons/${selectedContactPerson.id}`)
      .then((response) => {
        if (socket && id) {
          socket.emit("entity:saved", {
            modelType: "Client",
            recordId: parseInt(id, 10),
            changeType: "ContactPerson",
            changeAction: "Deleted",
            user: {
              id: currentUser?.id,
              name: currentUser?.Name || "User",
              imageThumbnail: currentUser?.ImageThumbnail,
            },
          });
        }
        triggerPageRefresh("Contact Person Deleted Successfully!", theme);
      })
      .catch((error) => {
        const message =
          error.response?.data?.error ||
          "Unable to delete contact person. Please try again.";
        showSnackbar(message, theme, "error");
      });
  };

  const requestorColumns = useMemo(
    () => [
      {
        field: "Name",
        headerName: "Name",
        minWidth: 220,
        flex: 1.4,
        valueGetter: (_, row) => row?.Name || "N/A",
      },
      {
        field: "PhoneNumber",
        headerName: "Phone Number",
        minWidth: 180,
        flex: 1.2,
        valueGetter: (_, row) => {
          const phones = row?.PhoneNumbers || [];
          const primary = phones.find((p) => p.IsPrimary) || phones[0];
          if (primary && primary.Number) {
            const cc = countryOptions.find(
              (c) => String(c.callingCode) === String(primary.CountryCode),
            )?.code || "US";
            const formatted = formatPhoneNumber(primary.Number, cc);
            const ext = primary.Ext ? ` x${primary.Ext}` : "";
            return `${formatted}${ext}`;
          }
          if (row?.PhoneNumber) {
            const ext = row.Ext ? ` x${row.Ext}` : "";
            return `${formatPhoneNumber(row.PhoneNumber, "US")}${ext}`;
          }
          return "N/A";
        },
      },
      {
        field: "Email",
        headerName: "Email",
        minWidth: 220,
        flex: 1.3,
        valueGetter: (_, row) => row?.Email || "N/A",
      },
      {
        field: "Group",
        headerName: "Group",
        minWidth: 130,
        flex: 1,
        valueGetter: (_, row) => row?.Group?.Name || "—",
      },
      {
        field: "createdAt",
        headerName: "Date Added",
        minWidth: 130,
        flex: 0.9,
        valueGetter: (_, row) =>
          row?.createdAt ? dayjs(row.createdAt).format("M/D/YYYY") : "N/A",
      },
      {
        field: "ServicesCount",
        headerName: "Total Projects",
        minWidth: 120,
        flex: 0.8,
        type: "number",
        align: "center",
        headerAlign: "center",
        valueGetter: (_, row) => Number(row?.ServicesCount || 0),
      },
      {
        field: "LastServiceDate",
        headerName: "Last Project",
        minWidth: 130,
        flex: 0.9,
        valueGetter: (_, row) => formatRelativeTime(row?.LastServiceDate),
      },
    ],
    [],
  );

  const contactPersonColumns = useMemo(
    () => [
      {
        field: "Name",
        headerName: "Name",
        minWidth: 220,
        flex: 1.8,
        valueGetter: (_, row) => row?.Name || "N/A",
      },
      {
        field: "PhoneNumber",
        headerName: "Phone Number",
        minWidth: 180,
        flex: 1.4,
        valueGetter: (_, row) => {
          const phones = row?.PhoneNumbers || [];
          const primary = phones.find((p) => p.IsPrimary) || phones[0];
          if (primary && primary.Number) {
            const cc = countryOptions.find(
              (c) => String(c.callingCode) === String(primary.CountryCode),
            )?.code || "US";
            const formatted = formatPhoneNumber(primary.Number, cc);
            const ext = primary.Ext ? ` x${primary.Ext}` : "";
            return `${formatted}${ext}`;
          }
          if (row?.PhoneNumber) {
            const ext = row.Ext ? ` x${row.Ext}` : "";
            return `${formatPhoneNumber(row.PhoneNumber, "US")}${ext}`;
          }
          return "N/A";
        },
      },
      {
        field: "Email",
        headerName: "Email",
        minWidth: 220,
        flex: 1.6,
        valueGetter: (_, row) => row?.Email || "N/A",
      },
      {
        field: "createdAt",
        headerName: "Date Added",
        minWidth: 130,
        flex: 1.0,
        valueGetter: (_, row) =>
          row?.createdAt ? dayjs(row.createdAt).format("M/D/YYYY") : "N/A",
      },
    ],
    [],
  );

  // Set entity tracking for presence and updates
  const { setCurrentEntity } = usePresence();
  usePagePresence();
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    if (id) {
      const entityId = parseInt(id);
      setCurrentEntity({
        entityType: "Client",
        entityId,
      });
    }
    return () => {
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [id, setCurrentEntity]);

  // Listen for client updates from other members
  const handleClientRefetch = useCallback(
    (updatedBy = {}, meta = {}) => {
      // Reload the client data
      api
        .all([
          api.get(`/clients/${id}`),
          api.get(`/clients/${id}/requestorsSummary`),
        ])
        .then(
          api.spread((clientResponse, requestorSummaryResponse) => {
            if (clientResponse) {
              setClientData(clientResponse.data);
            }
            if (requestorSummaryResponse) {
              const rawData = requestorSummaryResponse.data || {};
              const requestorArray = Array.isArray(rawData)
                ? rawData
                : Object.values(rawData);
              const normalized = requestorArray.map((r) => ({
                ...r,
                ServicesCount: r.ServicesCount ?? r.servicesCount ?? 0,
                LastServiceDate:
                  r.LastServiceDate ?? r.lastServiceDate ?? null,
              }));
              setListOfRequestorSummaries(normalized);
            }
          }),
        )
        .catch((err) => {
          if (err.response?.status === 404) {
            navigate("/404", { replace: true });
          } else {
            console.log("Error refetching client:", err);
          }
        });

      // Refresh dependent widgets (tasks, rates, etc.)
      triggerPageRefresh();

      // Store update meta for snackbar
      setLastUpdate({
        userName: updatedBy?.userName || "Another member",
        changeType: meta?.changeType,
        changeAction: meta?.changeAction,
      });
    },
    [id, navigate, triggerPageRefresh],
  );

  useEffect(() => {
    if (lastUpdate?.userName) {
      const action = lastUpdate.changeAction || "Updated";
      const changeType = lastUpdate.changeType || "Client";
      const message = `${changeType} ${action} by ${lastUpdate.userName}`;
      showSnackbar(message, theme, "info");
      setLastUpdate(null);
    }
  }, [lastUpdate, showSnackbar, theme]);

  useEntityUpdates({
    entityType: "Client",
    entityId: id ? parseInt(id, 10) : null,
    onUpdate: handleClientRefetch,
  });

  useAssignmentUpdates({
    modelType: "Client",
    recordId: id ? parseInt(id) : null,
    onUpdate: handleClientRefetch,
  });

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  // Scroll spy effect to update active mobile tab based on scroll position
  useEffect(() => {
    if (!isSmallScreen || !mobileScrollRef.current) return;

    const observerOptions = {
      root: mobileScrollRef.current,
      rootMargin: "-88px 0px -60% 0px",
      threshold: [0.1, 0.25, 0.5, 0.75],
    };

    const observerCallback = (entries) => {
      let mostVisible = null;
      let maxRatio = 0;

      entries.forEach((entry) => {
        if (entry.intersectionRatio > maxRatio) {
          maxRatio = entry.intersectionRatio;
          mostVisible = entry.target;
        }
      });

      if (mostVisible) {
        const index = parseInt(mostVisible.getAttribute("data-index"));
        if (!isNaN(index) && index !== mobileTabRef.current) {
          mobileTabRef.current = index;
          setMobileTabValue(index);
        }
      }
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    mobileSectionRefs.forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [isSmallScreen, mobileSectionRefs]);

  const sanitizeAddressDisplay = (value) => {
    const normalized = String(value || "")
      .replace(/\r\n?/g, "\n")
      .trim();
    if (!normalized) return "";

    const lines = normalized
      .split("\n")
      .map((part) => part.trim())
      .filter((part) => part && part.toUpperCase() !== "N/A");

    if (lines.length > 0) {
      return lines.join("\n");
    }

    const commaParts = normalized
      .split(",")
      .map((part) => part.trim())
      .filter((part) => part && part.toUpperCase() !== "N/A");

    return commaParts.join("\n");
  };

  const buildClientAddressDisplay = (client) => {
    const structuredLines = [
      client?.AddressLine1,
      client?.AddressLine2,
      client?.AddressLine3,
    ]
      .map((line) => String(line || "").trim())
      .filter((line) => line && line.toUpperCase() !== "N/A");

    if (structuredLines.length > 0) {
      return structuredLines.join("\n");
    }

    return sanitizeAddressDisplay(client?.MainAddress);
  };

  const accountInformation = [
    {
      id: 0,
      key: "0-" + clientData.ShortName,
      Title: "Short Name",
      Value: clientData.ShortName,
    },
    {
      id: 1,
      key:
        "1-" +
        [
          clientData.AddressLine1,
          clientData.AddressLine2,
          clientData.AddressLine3,
        ]
          .filter(Boolean)
          .join("|"),
      Title: "Address",
      Value: buildClientAddressDisplay(clientData),
    },
    {
      id: 2,
      key: "2-" + clientData.Type,
      Title: "Type",
      Value: clientData.Type,
    },
    {
      id: 3,
      key: "3-" + clientData.OPIMinimumRate,
      Title: "OPI Minimum Rate",
      Value: clientData.OPIMinimumRate && "$" + clientData.OPIMinimumRate,
    },
    {
      id: 4,
      key: "4-" + clientData.InvoiceNoteTemplate,
      Title: "Invoice Note Template",
      Value: clientData.InvoiceNoteTemplate,
    },
    {
      id: 5,
      key: "5-" + clientData.Timezone,
      Title: "Timezone",
      Value: clientData.Timezone || null,
    },
  ];

  const copyToClipboard = useCallback(
    async (value, label) => {
      if (!value) return;
      try {
        await navigator.clipboard.writeText(String(value));
        showSnackbar(`${label} copied`, theme, "success");
      } catch {
        showSnackbar(`Failed to copy ${label.toLowerCase()}`, theme, "error");
      }
    },
    [showSnackbar, theme],
  );

  const actionIconButtonSx = {
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: "8px",
    p: 0.25,
    width: 24,
    height: 24,
    "& .MuiSvgIcon-root": {
      fontSize: 16,
    },
    "&:hover": {
      borderColor: theme.palette.primary.buttonHoverSelected,
      backgroundColor: theme.palette.primary.buttonHoverSelected,
      color: theme.palette.background.default,
    },
  };

  const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    const normalized = String(value).trim();
    return normalized !== "" && normalized.toUpperCase() !== "N/A";
  };

  const renderAccountInfoActions = (item) => {
    if (item?.Title !== "Address" || !hasValue(item?.Value)) return null;

    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      item.Value,
    )}`;

    return (
      <Stack direction="row" spacing={0.75}>
        <IconButton
          component="a"
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          aria-label="Open in Google Maps"
          sx={actionIconButtonSx}
        >
          <MapRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label="Copy address"
          onClick={() => copyToClipboard(item.Value, "Address")}
          sx={actionIconButtonSx}
        >
          <ContentCopyRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>
    );
  };

  const deleteRequestor = () => {
    api
      .post("/clients/deleteRequestor", {
        data: {
          id: selectedRequestor.id,
        },
      })
      .then((response) => {
        if (socket && id) {
          socket.emit("entity:saved", {
            modelType: "Client",
            recordId: parseInt(id, 10),
            changeType: "Requestor",
            changeAction: "Deleted",
            user: {
              id: currentUser?.id,
              name: currentUser?.Name || "User",
              imageThumbnail: currentUser?.ImageThumbnail,
            },
          });
        }
        triggerPageRefresh("Requestor Deleted Successfully!", theme);
      })
      .catch((error) => {
        const message =
          error.response?.data?.error ||
          "Unable to delete requestor. Please try again.";
        showSnackbar(message, theme, "error");
      });
  };
  const handleOpenEditOverlay = useCallback(() => {
    setOverlayData({
      ClientID: id,
      Client: clientData,
      ClientTypes: listofClientsTypes,
      ContractStatuses: listofContractStatuses,
      ClientServiceTypes: listofClientServiceTypes,
      PaymentMethods: listOfPaymentMethods,
      Action: OVERLAY_NAMES.CLIENT_ACTION_EDIT,
    });

    setOverlayName(OVERLAY_NAMES.CLIENT_NEW_EDIT);
  }, [
    clientData,
    id,
    listofClientsTypes,
    listofClientServiceTypes,
    listOfPaymentMethods,
    listofContractStatuses,
    setOverlayData,
    setOverlayName,
  ]);
  const isMobile = !useMediaQuery("(min-width:600px)");

  return (
    <Box
      p={isSmallScreen ? "0.5rem 0.5rem 0 0.5rem" : "1rem 1rem 0 1rem"}
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`,
        minHeight: 0,
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Grid container alignItems="center">
          <Grid
            size={{ xs: 12 }}
            container
            justifyContent="flex-end"
            alignItems="center"
            spacing={0}
            sx={{ pr: 1 }}
          >
            {!isMobile ? (
              <Box display="flex" gap={2} alignItems="center">
                <Button
                  onClick={handleOpenEditOverlay}
                  startIcon={<EditRoundedIcon />}
                  sx={{
                    width: "200px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    padding: "8px 16px",
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.background.default,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "& .MuiButton-startIcon": {
                      marginRight: 0,
                    },
                    "& .MuiButton-endIcon": {
                      display: "none",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <Box sx={{ flex: 1, textAlign: "center" }}>Edit Client</Box>
                </Button>
              </Box>
            ) : (
              <Box display="flex" gap={2} alignItems="center">
                <Button
                  onClick={handleOpenEditOverlay}
                  sx={{
                    width: "40px",
                    minWidth: "40px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.background.default,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "& .MuiButton-startIcon": {
                      margin: 0,
                    },
                    "& .MuiButton-endIcon": {
                      display: "none",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <EditRoundedIcon />
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      <Box
        id="test"
        borderRadius="12px"
        bgcolor={theme.palette.background.alt}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          overflow: isSmallScreen ? "auto" : "hidden",
          minHeight: 0,
          height: "100%",
          pb: 1,
          px: 1,
          pt: 2,
        }}
      >
        <Grid
          container
          spacing={isSmallScreen ? 1 : 2}
          sx={{
            flex: 1,
            minHeight: 0,
            height: "100%",
            display: "grid",
            gridTemplateColumns: isSmallScreen
              ? "1fr"
              : "minmax(0, min(20vw, 400px)) auto minmax(0, 1fr)",
            gridTemplateRows: "minmax(0, 1fr)",
            alignItems: "stretch",
          }}
        >
          <Grid
            size={{ xs: 12, md: "auto" }}
            sx={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              maxWidth: "100%",
              overflow: "hidden",
              height: isSmallScreen ? "auto" : "100%",
            }}
          >
            <Box
              sx={{
                overflowY: "auto",
                overflowX: "hidden",
                flex: 1,
                width: "100%",
                height: "100%",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {/* SettingsCard Component Definition */}
              {(() => {
                const SettingsCard = ({ children, title, onEdit }) => (
                  <Box
                    sx={{
                      bgcolor: theme.palette.background.default,
                      borderRadius: "12px",
                      border: `1px solid ${theme.palette.primary.border2}`,
                      p: isSmallScreen ? "0.75rem" : "1.25rem",
                      m: "0.25rem",
                      boxShadow: theme.shadows[3],
                      minWidth: 0,
                      width: "calc(100% - 0.5rem)",
                      maxWidth: "calc(100% - 0.5rem)",
                      flexShrink: 0, // card keeps its natural height
                      overflow: "hidden", // width protection only (text wraps inside)
                    }}
                  >
                    {title && (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            mb: 2,
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            variant={isSmallScreen ? "h4" : "h3"}
                            fontWeight="bold"
                            sx={{
                              minWidth: 0,
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                            }}
                          >
                            {title}
                          </Typography>
                          {onEdit && (
                            <IconButton
                              aria-label="Edit"
                              onClick={onEdit}
                              sx={{ alignSelf: "flex-start" }}
                            >
                              <EditRoundedIcon />
                            </IconButton>
                          )}
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                      </>
                    )}
                    {children}
                  </Box>
                );

                return (
                  <>
                    {/* Account Information Card */}
                    <SettingsCard title="Account Information">
                      {accountInformation.every(
                        (element) => element.Value === null,
                      ) ? (
                        <Typography variant="body2">
                          No information available.
                        </Typography>
                      ) : (
                        <>
                          {accountInformation.map((item, index) =>
                            item.Value === null ? null : (
                              <Box
                                key={
                                  "AccountInfo-" +
                                  index +
                                  "-" +
                                  item.Value +
                                  "-" +
                                  item.Title
                                }
                                sx={{
                                  mb:
                                    index <
                                    accountInformation.filter((i) => i.Value)
                                      .length -
                                      1
                                      ? 2
                                      : 0,
                                }}
                              >
                                <Box sx={{ mb: 1 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        minWidth: 0,
                                        overflowWrap: "anywhere",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {item.Title}
                                    </Typography>
                                    {renderAccountInfoActions(item)}
                                  </Box>
                                  {item.Title === "Address" ? (
                                    <Box sx={{ mt: 0.25 }}>
                                      {String(item.Value || "")
                                        .split("\n")
                                        .filter((line) => line.trim() !== "")
                                        .map((line, lineIndex) => (
                                          <Typography
                                            key={`address-line-${lineIndex}`}
                                            variant="body1"
                                            sx={{
                                              overflowWrap: "anywhere",
                                              wordBreak: "break-word",
                                            }}
                                          >
                                            {line}
                                          </Typography>
                                        ))}
                                    </Box>
                                  ) : (
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        mt: 0.25,
                                        overflowWrap: "anywhere",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {item.Value}
                                    </Typography>
                                  )}
                                </Box>
                                {index <
                                  accountInformation.filter((i) => i.Value)
                                    .length -
                                    1 && <Divider sx={{ my: 1 }} />}
                              </Box>
                            ),
                          )}
                        </>
                      )}
                    </SettingsCard>

                    {/* Details Card */}
                    <SettingsCard title="Details">
                      <>
                        {/* Service Types */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Service Types
                          </Typography>
                          <Box sx={{ mt: 1, ml: 2, minWidth: 0 }}>
                            {clientData.ClientServiceTypes &&
                            clientData.ClientServiceTypes.length > 0 ? (
                              clientData.ClientServiceTypes.map(
                                (item, index) => (
                                  <Typography
                                    key={
                                      "Details-" +
                                      index +
                                      "-" +
                                      item.ServiceType.Name +
                                      "-" +
                                      item.ServiceType.id
                                    }
                                    variant="body2"
                                    sx={{
                                      overflowWrap: "anywhere",
                                      wordBreak: "break-word",
                                      mb:
                                        index <
                                        clientData.ClientServiceTypes.length - 1
                                          ? 0.5
                                          : 0,
                                    }}
                                  >
                                    • {item.ServiceType.Name}
                                  </Typography>
                                ),
                              )
                            ) : (
                              <Typography variant="body2">N/A</Typography>
                            )}
                          </Box>
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        {/* Payment Methods */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Payment Methods
                          </Typography>
                          <Box sx={{ mt: 1, ml: 2, minWidth: 0 }}>
                            {clientData.ClientPaymentMethods &&
                            clientData.ClientPaymentMethods.length > 0 ? (
                              clientData.ClientPaymentMethods.map(
                                (item, index) => (
                                  <Typography
                                    key={`PaymentMethod-${item.id}-${item.PaymentMethod?.id || index}`}
                                    variant="body2"
                                    sx={{
                                      overflowWrap: "anywhere",
                                      wordBreak: "break-word",
                                      mb:
                                        index <
                                        clientData.ClientPaymentMethods.length -
                                          1
                                          ? 0.5
                                          : 0,
                                    }}
                                  >
                                    • {item.PaymentMethod?.Name || "N/A"}
                                  </Typography>
                                ),
                              )
                            ) : (
                              <Typography variant="body2">N/A</Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Contract Status */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Contract Status
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              mt: 0.25,
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                            }}
                          >
                            {clientData.ClientContractHistories &&
                            clientData.ClientContractHistories.length > 0
                              ? clientData.ClientContractHistories[0].Status
                              : "N/A"}
                          </Typography>
                        </Box>

                        {/* Contract History */}
                        {clientData.ClientContractHistories &&
                        clientData.ClientContractHistories.length > 0 ? (
                          <>
                            <Divider sx={{ my: 1.5 }} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Contract History
                              </Typography>
                              <Box sx={{ mt: 1, ml: 2, minWidth: 0 }}>
                                {clientData.ClientContractHistories.length ===
                                1 ? (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      overflowWrap: "anywhere",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    •{" "}
                                    {
                                      clientData.ClientContractHistories[0]
                                        .Member.Name
                                    }{" "}
                                    created the contract with status of{" "}
                                    {
                                      clientData.ClientContractHistories[0]
                                        .Status
                                    }
                                    , on{" "}
                                    {clientData.ClientContractHistories[0].Date}{" "}
                                    at{" "}
                                    {clientData.ClientContractHistories[0].Time}
                                  </Typography>
                                ) : (
                                  clientData.ClientContractHistories.map(
                                    (item, index) => (
                                      <Typography
                                        key={
                                          "ContractHistory-" +
                                          index +
                                          "-" +
                                          item.id
                                        }
                                        variant="body2"
                                        sx={{
                                          overflowWrap: "anywhere",
                                          wordBreak: "break-word",
                                          mb:
                                            index <
                                            clientData.ClientContractHistories
                                              .length -
                                              1
                                              ? 0.5
                                              : 0,
                                        }}
                                      >
                                        • {item.Member.Name} updated the
                                        contract to the status of {item.Status},
                                        on {formatDate(item.Date)} at{" "}
                                        {item.Time}
                                      </Typography>
                                    ),
                                  )
                                )}
                              </Box>
                            </Box>
                          </>
                        ) : null}
                      </>
                    </SettingsCard>
                  </>
                );
              })()}
            </Box>
          </Grid>

          <Divider
            orientation={isSmallScreen ? "horizontal" : "vertical"}
            flexItem
          />

          <Grid
            size={{ xs: 12, sm: 12, md: "grow" }}
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              minWidth: 0,
              overflow: "hidden",
              flexGrow: 1,
              height: "100%",
            }}
          >
            <Box
              ref={mobileScrollRef}
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                width: "100%",
                height: "100%",
                minHeight: 0,
                overflowY: isMobile ? "auto" : "hidden",
              }}
            >
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  minWidth: 0,
                  width: "100%",
                  overflow: "visible",
                  ...(isMobile
                    ? {
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        bgcolor: theme.palette.background.alt,
                        flexShrink: 0,
                      }
                    : {}),
                }}
              >
                <Tabs
                  value={isMobile ? mobileTabValue : tabValue}
                  onChange={isMobile ? handleMobileTabsChange : handleTabsChange}
                  aria-label="basic tabs example"
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    bgcolor: theme.palette.background.alt,
                    "& .MuiTab-root": {
                      borderRadius: "12px",
                    },
                  }}
                >
                  <CustomTab label="Requestors" {...a11yProps(0)} />
                  <CustomTab label="Groups" {...a11yProps(1)} />
                  <CustomTab label="Contact Persons" {...a11yProps(2)} />
                  <CustomTab label="Tasks" {...a11yProps(3)} />
                  {/** <CustomTab label="Sub Accounts" {...a11yProps(1)} />*/}
                  <CustomTab label="Projects" {...a11yProps(4)} />
                  <CustomTab label="Interpretation Rates" {...a11yProps(5)} />
                  <CustomTab label="Notes" {...a11yProps(6)} />
                  <CustomTab label="History" {...a11yProps(7)} />
                </Tabs>
              </Box>

              <DragDropContext onDragEnd={handleTaskDragEnd}>
                <CustomTabPanel
                  value={isMobile ? 0 : tabValue}
                  index={0}
                  key="Tab One"
                  ref={mobileSectionRefs[0]}
                  data-index={0}
                  stacked={isMobile}
                  sx={{
                    flex: 1,
                    width: "100%",
                    minHeight: 0,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      p: "0.25rem",
                    }}
                  >
                    <DataGrid
                      rows={listOfRequestorSummaries || []}
                      columns={requestorColumns}
                      getRowId={(row) => row.id}
                      apiRef={requestorsApiRef}
                      density="compact"
                      disableRowSelectionOnClick
                      pageSizeOptions={[10, 25, 50, 100]}
                      initialState={{
                        pagination: {
                          paginationModel: { pageSize: 25, page: 0 },
                        },
                        sorting: {
                          sortModel: [{ field: "Name", sort: "asc" }],
                        },
                      }}
                      sx={{
                        borderRadius: "12px",
                        boxShadow: theme.shadows[3],
                        border: `1px solid ${theme.palette.primary.border2}`,
                        bgcolor: theme.palette.background.default,

                        "& .MuiDataGrid-columnHeaderTitle": {
                          fontWeight: "bold",
                          color: theme.palette.primary.main,
                        },
                        "& .MuiDataGrid-scrollbarFiller": {
                          backgroundColor: theme.palette.background.alt,
                        },
                        "& .MuiDataGrid-menuIconButton": {
                          opacity: 0,
                        },
                        "& .MuiDataGrid-sortIcon": {
                          opacity: 0,
                        },
                        "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                          flex: 1,
                        },
                        "& .MuiTypography-root.MuiTypography-body1": {
                          textAlign: "left !important",
                        },
                        "& .MuiDataGrid-columnSeparator": {
                          color: theme.palette.primary.main,
                        },
                        "& .MuiButton-root": {
                          color: theme.palette.primary.main,
                        },
                        "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus":
                          {
                            outline: "none !important",
                          },
                        "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
                          {
                            outline: "none !important",
                          },
                      }}
                      slotProps={{
                        row: {
                          onContextMenu: handleRequestorGridContextMenu,
                          style: { cursor: "context-menu" },
                        },
                      }}
                    />
                  </Box>

                  <Menu
                    open={requestorContextMenu !== null}
                    onClose={handleRequestorMenuClose}
                    anchorReference="anchorPosition"
                    anchorPosition={
                      requestorContextMenu !== null
                        ? {
                            top: requestorContextMenu.mouseY,
                            left: requestorContextMenu.mouseX,
                          }
                        : undefined
                    }
                    slotProps={{
                      paper: {
                        sx: {
                          bgcolor: theme.palette.background.alt,
                          border: `1px solid ${theme.palette.divider}`,
                          "& .MuiListItemIcon-root": {
                            color: theme.palette.primary.main,
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        setOverlayData({
                          ClientID: id,
                          Requestor: selectedRequestor,
                          Action: OVERLAY_NAMES.REQUESTOR_ACTION_EDIT,
                        });
                        setOverlayName(OVERLAY_NAMES.REQUESTOR_NEW_EDIT);

                        handleRequestorMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <EditRoundedIcon fontSize="small" />
                      </ListItemIcon>
                      <Typography variant="inherit">Edit</Typography>
                    </MenuItem>
                    {canCallSelectedRequestor && (
                      <MenuItem onClick={callSelectedRequestor}>
                        <ListItemIcon>
                          <LocalPhoneRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        <Typography variant="inherit">Call</Typography>
                      </MenuItem>
                    )}
                    {canEmailSelectedRequestor && (
                      <MenuItem onClick={emailSelectedRequestor}>
                        <ListItemIcon>
                          <EmailRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        <Typography variant="inherit">Email</Typography>
                      </MenuItem>
                    )}
                    <MenuItem
                      onClick={() => {
                        openMergeRequestorOverlay();
                      }}
                    >
                      <ListItemIcon>
                        <SwapHorizRoundedIcon fontSize="small" />
                      </ListItemIcon>
                      <Typography variant="inherit">Merge</Typography>
                    </MenuItem>
                    <MenuItem
                      disabled={!canDeleteSelectedRequestor}
                      onClick={() => {
                        if (!canDeleteSelectedRequestor) {
                          return;
                        }

                        openDialog({
                          theme: theme,
                          title: "Delete Confirmation",
                          message:
                            "Are you sure you'd like to delete Requestor: " +
                            selectedRequestor?.Name +
                            "?",

                          customContent: null,

                          tertiaryAction: null,
                          secondaryAction: "Close",
                          primaryAction: "Delete",

                          tertiaryOnClick: null,

                          secondaryOnClick: () => {
                            closeDialog();
                          },

                          primaryOnClick: () => {
                            deleteRequestor();
                            closeDialog();
                            handleRequestorMenuClose();
                          },
                        });
                      }}
                    >
                      <ListItemIcon>
                        <DeleteRoundedIcon fontSize="small" />
                      </ListItemIcon>
                      <Typography variant="inherit">
                        {canDeleteSelectedRequestor
                          ? "Delete"
                          : "Delete (Has Services)"}
                      </Typography>
                    </MenuItem>
                  </Menu>

                  <Fab
                    color="primary"
                    aria-label="add"
                    sx={{
                      position: "absolute",
                      bottom: 90,
                      right: 40,
                      zIndex: 2,
                      borderRadius: "12px",
                      width: 48,
                      height: 48,
                    }}
                    onClick={() => {
                      setOverlayData({
                        ClientID: id,
                        Action: OVERLAY_NAMES.REQUESTOR_ACTION_NEW,
                      });
                      setOverlayName(OVERLAY_NAMES.REQUESTOR_NEW_EDIT);
                    }}
                  >
                    <AddRoundedIcon
                      sx={{
                        color: theme.palette.background.default,
                        fontSize: 24,
                      }}
                    />
                  </Fab>
                </CustomTabPanel>

                <CustomTabPanel
                  value={isMobile ? 1 : tabValue}
                  index={1}
                  key="Tab Groups"
                  ref={mobileSectionRefs[1]}
                  data-index={1}
                  stacked={isMobile}
                  sx={{
                    flex: 1,
                    width: "100%",
                    minHeight: 0,
                  }}
                >
                  <RequestorGroupsTab clientId={id} />
                </CustomTabPanel>

                <CustomTabPanel
                  value={isMobile ? 2 : tabValue}
                  index={2}
                  key="Tab Contact Persons"
                  ref={mobileSectionRefs[2]}
                  data-index={2}
                  stacked={isMobile}
                  sx={{
                    flex: 1,
                    width: "100%",
                    minHeight: 0,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      p: "0.25rem",
                    }}
                  >
                    <DataGrid
                      rows={listOfContactPersons || []}
                      columns={contactPersonColumns}
                      getRowId={(row) => row.id}
                      apiRef={contactPersonsApiRef}
                      density="compact"
                      disableRowSelectionOnClick
                      pageSizeOptions={[10, 25, 50, 100]}
                      initialState={{
                        pagination: {
                          paginationModel: { pageSize: 25, page: 0 },
                        },
                        sorting: {
                          sortModel: [{ field: "Name", sort: "asc" }],
                        },
                      }}
                      sx={{
                        borderRadius: "12px",
                        boxShadow: theme.shadows[3],
                        border: `1px solid ${theme.palette.primary.border2}`,
                        bgcolor: theme.palette.background.default,

                        "& .MuiDataGrid-columnHeaderTitle": {
                          fontWeight: "bold",
                          color: theme.palette.primary.main,
                        },
                        "& .MuiDataGrid-scrollbarFiller": {
                          backgroundColor: theme.palette.background.alt,
                        },
                        "& .MuiDataGrid-menuIconButton": {
                          opacity: 0,
                        },
                        "& .MuiDataGrid-sortIcon": {
                          opacity: 0,
                        },
                        "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                          flex: 1,
                        },
                        "& .MuiTypography-root.MuiTypography-body1": {
                          textAlign: "left !important",
                        },
                        "& .MuiDataGrid-columnSeparator": {
                          color: theme.palette.primary.main,
                        },
                        "& .MuiButton-root": {
                          color: theme.palette.primary.main,
                        },
                        "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus":
                          {
                            outline: "none !important",
                          },
                        "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
                          {
                            outline: "none !important",
                          },
                      }}
                      slotProps={{
                        row: {
                          onContextMenu: handleContactPersonGridContextMenu,
                          style: { cursor: "context-menu" },
                        },
                      }}
                    />
                  </Box>

                  <Menu
                    open={contactPersonContextMenu !== null}
                    onClose={handleContactPersonMenuClose}
                    anchorReference="anchorPosition"
                    anchorPosition={
                      contactPersonContextMenu !== null
                        ? {
                            top: contactPersonContextMenu.mouseY,
                            left: contactPersonContextMenu.mouseX,
                          }
                        : undefined
                    }
                    slotProps={{
                      paper: {
                        sx: {
                          bgcolor: theme.palette.background.alt,
                          border: `1px solid ${theme.palette.divider}`,
                          "& .MuiListItemIcon-root": {
                            color: theme.palette.primary.main,
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        setOverlayData({
                          ClientID: id,
                          ContactPerson: selectedContactPerson,
                          Action: OVERLAY_NAMES.CONTACT_PERSON_ACTION_EDIT,
                        });
                        setOverlayName(OVERLAY_NAMES.CONTACT_PERSON_NEW_EDIT);

                        handleContactPersonMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <EditRoundedIcon fontSize="small" />
                      </ListItemIcon>
                      <Typography variant="inherit">Edit</Typography>
                    </MenuItem>
                    {canCallSelectedContactPerson && (
                      <MenuItem onClick={callSelectedContactPerson}>
                        <ListItemIcon>
                          <LocalPhoneRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        <Typography variant="inherit">Call</Typography>
                      </MenuItem>
                    )}
                    {canEmailSelectedContactPerson && (
                      <MenuItem onClick={emailSelectedContactPerson}>
                        <ListItemIcon>
                          <EmailRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        <Typography variant="inherit">Email</Typography>
                      </MenuItem>
                    )}
                    <MenuItem
                      onClick={() => {
                        openMergeContactPersonOverlay();
                      }}
                    >
                      <ListItemIcon>
                        <SwapHorizRoundedIcon fontSize="small" />
                      </ListItemIcon>
                      <Typography variant="inherit">Merge</Typography>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        openDialog({
                          theme: theme,
                          title: "Delete Confirmation",
                          message:
                            "Are you sure you'd like to delete Contact Person: " +
                            selectedContactPerson?.Name +
                            "?",

                          customContent: null,

                          tertiaryAction: null,
                          secondaryAction: "Close",
                          primaryAction: "Delete",

                          tertiaryOnClick: null,

                          secondaryOnClick: () => {
                            closeDialog();
                          },

                          primaryOnClick: () => {
                            deleteContactPerson();
                            closeDialog();
                            handleContactPersonMenuClose();
                          },
                        });
                      }}
                    >
                      <ListItemIcon>
                        <DeleteRoundedIcon fontSize="small" />
                      </ListItemIcon>
                      <Typography variant="inherit">Delete</Typography>
                    </MenuItem>
                  </Menu>

                  <Fab
                    color="primary"
                    aria-label="add"
                    sx={{
                      position: "absolute",
                      bottom: 90,
                      right: 40,
                      zIndex: 2,
                      borderRadius: "12px",
                      width: 48,
                      height: 48,
                    }}
                    onClick={() => {
                      setOverlayData({
                        ClientID: id,
                        Action: OVERLAY_NAMES.CONTACT_PERSON_ACTION_NEW,
                      });
                      setOverlayName(OVERLAY_NAMES.CONTACT_PERSON_NEW_EDIT);
                    }}
                  >
                    <AddRoundedIcon
                      sx={{
                        color: theme.palette.background.default,
                        fontSize: 24,
                      }}
                    />
                  </Fab>
                </CustomTabPanel>

                <CustomTabPanel
                  value={isMobile ? 3 : tabValue}
                  index={3}
                  key="Tab Two"
                  ref={mobileSectionRefs[3]}
                  data-index={3}
                  stacked={isMobile}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    height: "100%",
                    pt: 0,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      width: "100%",
                      height: "100%",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      pt: 1,
                    }}
                  >
                    <TaskBoard
                      statuses={taskStatuses}
                      tasks={tasksForService}
                      isLoading={tasksLoading}
                      onDragEnd={handleTaskDragEnd}
                      renderTaskCard={renderTaskCard}
                      creatingTaskForStatus={creatingTaskForStatus}
                      newTaskTitle={newTaskTitle}
                      setNewTaskTitle={setNewTaskTitle}
                      handleCreateInlineTask={handleCreateInlineTask}
                      setCreatingTaskForStatus={setCreatingTaskForStatus}
                      enableInlineCreate={true}
                      columnsOverride={2}
                      theme={theme}
                      heightFraction={1}
                      rowHeightFraction={0.5}
                      containerPadding="0.25rem"
                      containerMargin="0rem"
                      gridSpacing={2}
                      externalDragContext={true}
                      constrainToParentHeight={true}
                    />
                  </Box>
                  {/* Removed priority color code popover */}
                </CustomTabPanel>
                <CustomTabPanel
                  value={isMobile ? 4 : tabValue}
                  index={4}
                  key="Tab Three"
                  ref={mobileSectionRefs[4]}
                  data-index={4}
                  stacked={isMobile}
                  sx={{
                    flex: 1,
                    width: "100%",
                    minHeight: 0,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      width: "100%",

                      display: "flex",
                      flexDirection: "column",
                      p: "0.25rem",
                      [`.${gridClasses.cell}`]: {
                        color: theme.palette.primary.text,
                      },
                      [`.${gridClasses.cell}.notAvailable`]: {
                        color: theme.palette.primary.text3,
                      },
                    }}
                  >
                    <DataGrid
                      loading={isLoading}
                      pageSizeOptions={[20, 50, 100]}
                      getRowId={(row) => row.id}
                      rows={columnFilteredData || []}
                      apiRef={historyApiRef}
                      columns={columns}
                      sortModel={sortModel}
                      filterModel={filterModel}
                      onFilterModelChange={handleFilterModelChange}
                      onStateChange={(e) => {
                        const lookup = e.filter?.filteredRowsLookup;
                        if (!lookup) return;

                        const nextIds = Object.keys(lookup).filter(
                          (key) => lookup[key],
                        );
                        nextIds.sort();

                        const prevIds = prevFilteredRowIdsRef.current;
                        const isSame =
                          prevIds.length === nextIds.length &&
                          prevIds.every((id, idx) => id === nextIds[idx]);

                        if (!isSame) {
                          prevFilteredRowIdsRef.current = nextIds;
                          setFilterRowIds(nextIds);
                        }
                      }}
                      rowSelectionModel={openFilter ? [] : undefined}
                      disableRowSelectionOnClick={!!openFilter}
                      onRowClick={(params, event, details) => {
                        if (openFilter) {
                          setOpenFilter(null);
                          return;
                        }

                        let sendTo;

                        let data;

                        switch (params.row.ServiceCategory) {
                          case "Interpretation":
                            sendTo = "interpretations";
                            data = {
                              Interpretation: params.row,
                              InterpretationID: params.row.id,
                              ComingFrom: `/clients/${id}`,
                            };
                            break;
                          case "Translation":
                            sendTo = "translations";
                            data = {
                              Translation: params.row,
                              TranslationID: params.row.id,
                              ComingFrom: `/clients/${id}`,
                            };
                            break;
                          case "Other":
                            sendTo = "otherServices";
                            data = {
                              OtherService: params.row,
                              OtherServiceID: params.row.id,
                              ComingFrom: `/clients/${id}`,
                            };
                            break;
                          default:
                            return;
                        }

                        navigate(`/${sendTo}/${params.row.id}`, {
                          state: data,
                        });
                      }}
                      getCellClassName={(params) => {
                        return params.value === "✘" ||
                          params.value === "N/A" ||
                          params.value === "0%" ||
                          params.value === "null"
                          ? "notAvailable"
                          : "";
                      }}
                      slots={{
                        noRowsOverlay: () => (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              height: "100%",
                            }}
                          >
                            <Typography variant="h4">
                              We have no assignments with {clientData.Account}{" "}
                              on {formatSelectedMonths(selectedMonths)}{" "}
                              {selectedYear}.
                            </Typography>
                          </Box>
                        ),
                        toolbar: () => (
                          <GridToolbarContainer
                            sx={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Box m="1"></Box>

                            <Box
                              sx={{
                                position: "absolute",
                                left: "50%",
                                transform: "translateX(-50%)",
                              }}
                            >
                              {hasAnyActiveFilters && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<ClearIcon />}
                                  onClick={handleClearAllFilters}
                                  sx={{
                                    color: theme.palette.primary.main,
                                    borderColor: theme.palette.primary.main,
                                    "&:hover": {
                                      borderColor:
                                        theme.palette.primary
                                          .buttonHoverSelected,
                                      backgroundColor:
                                        theme.palette.primary
                                          .buttonHoverSelected,
                                      color: theme.palette.background.default,
                                    },
                                  }}
                                >
                                  Clear Filters
                                </Button>
                              )}
                            </Box>

                            <Box
                              display="flex"
                              alignItems="end"
                              justifyContent="flex-end"
                              width="100%"
                              gap={2}
                            >
                              <Box
                                sx={{
                                  width: "100%",
                                  maxWidth: FILTER_SELECTOR_WIDTH,
                                  pr: 1,
                                }}
                              >
                                <CustomDateSelector
                                  label="Date Range"
                                  months={months}
                                  years={
                                    yearOptions.length > 0
                                      ? yearOptions
                                      : [String(currentYear)]
                                  }
                                  selectedMonths={tempSelectedMonths}
                                  selectedYears={tempSelectedYear}
                                  enabledYears={availableYears}
                                  enabledMonthsByYear={availableMonthsByYear}
                                  onSave={handleDateSelectionSave}
                                  isMultiMonth={true}
                                  isMultiYear={false}
                                  monthGridItemSize={3}
                                  yearGridItemSize={4}
                                  showClearFilter={false}
                                  useInnerShadow
                                />
                              </Box>
                            </Box>
                            <Box m="1"></Box>
                          </GridToolbarContainer>
                        ),
                        footer: customFooter,
                      }}
                      slotProps={{
                        row: {},
                        loadingOverlay: {
                          variant: "skeleton",
                          noRowsVariant: "skeleton",
                        },
                        toolbar: {
                          showQuickFilter: true,
                          printOptions: { disableToolbarButton: true },
                          csvOptions: { disableToolbarButton: true },
                        },
                      }}
                      ignoreDiacritics
                      sx={{
                        height: "100%",
                        width: "100%",
                        backgroundColor: theme.palette.background.default,
                        borderRadius: "12px",
                        boxShadow: theme.shadows[3],

                        "& .MuiDataGrid-columnHeaderTitle": {
                          fontWeight: "bold",
                          color: theme.palette.primary.main,
                        },
                        "& .MuiDataGrid-scrollbarFiller": {
                          backgroundColor: theme.palette.background.alt,
                        },
                        "& .MuiDataGrid-menuIconButton": {
                          opacity: 0,
                        },
                        "& .MuiDataGrid-sortIcon": {
                          opacity: 0,
                        },
                        "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                          flex: 1,
                        },
                        "& .MuiTypography-root.MuiTypography-body1": {
                          textAlign: "left !important",
                        },
                        "& .MuiDataGrid-columnSeparator": {
                          color: theme.palette.primary.main,
                        },
                        "& .MuiButton-root": {
                          color: theme.palette.primary.main,
                        },
                        "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus":
                          {
                            outline: "none !important",
                          },
                        "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
                          {
                            outline: "none !important",
                          },
                      }}
                      disableColumnMenu
                      disableColumnSorting
                    />
                  </Box>
                </CustomTabPanel>

                <CustomTabPanel
                  value={isMobile ? 5 : tabValue}
                  index={5}
                  key="Tab Four"
                  ref={mobileSectionRefs[5]}
                  data-index={5}
                  stacked={isMobile}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                      p: "0.25rem",
                      position: "relative",
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <Card
                          subtitle={"Default Rate"}
                          content={`$${Number(
                            clientRates?.find(
                              (r) =>
                                (r?.Language?.Name || "").toUpperCase() ===
                                "ALL",
                            )?.Rate ??
                              clientData?.OPIMinimumRate ??
                              0,
                          ).toLocaleString()}`}
                          theme={theme}
                          justify="center"
                        />
                      </Grid>

                      {clientRates &&
                      clientRates.filter(
                        (r) =>
                          (r?.Language?.Name || "").toUpperCase() !== "ALL",
                      ).length > 0 ? (
                        <Grid size={{ xs: 12 }}>
                          <Card2
                            allowShowMore={true}
                            title="Exceptions"
                            padding={2}
                            items={clientRates
                              .filter(
                                (rate) =>
                                  (rate?.Language?.Name || "").toUpperCase() !==
                                  "ALL",
                              )
                              .map((rate) => ({
                                subtitle:
                                  rate?.Language?.Name || "Unknown Language",
                                content: `$${Number(
                                  rate?.Rate ?? 0,
                                ).toLocaleString()}`,
                              }))}
                            justify="start"
                          />
                        </Grid>
                      ) : (
                        <Grid size={{ xs: 12 }}>
                          <Typography variant="h5">
                            No exception rates.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>

                    <Fab
                      color="primary"
                      aria-label="add"
                      sx={{
                        position: "absolute",
                        bottom: 10,
                        right: 10,
                        borderRadius: "12px",
                        width: 48,
                        height: 48,
                      }}
                      onClick={() => {
                        const filteredLanguages = listOfLanguages.filter(
                          (lang) => {
                            return !clientRates.some(
                              (rate) => rate.Language.id === lang.id,
                            );
                          },
                        );

                        setOverlayData({
                          ClientID: id,
                          Action: OVERLAY_NAMES.RATE_ACTION_NEW,
                          Languages: filteredLanguages,
                          ClientRates: clientRates,
                        });

                        setOverlayName(OVERLAY_NAMES.RATE_NEW_EDIT);
                      }}
                    >
                      <AppRegistrationRoundedIcon
                        sx={{
                          color: theme.palette.background.default,
                          fontSize: 24,
                        }}
                      />
                    </Fab>
                  </Box>
                </CustomTabPanel>

                <CustomTabPanel
                  value={isMobile ? 6 : tabValue}
                  index={6}
                  key="Tab Five"
                  ref={mobileSectionRefs[6]}
                  data-index={6}
                  stacked={isMobile}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    overflowY: "auto",
                  }}
                >
                  <Box
                    sx={{
                      p: "0.25rem",
                      position: "relative",
                      minHeight: "100%",
                    }}
                  >
                    <Grid container spacing={2}>
                      {clientNotes.length > 0 ? (
                        clientNotes.map((note) => (
                          <Grid
                            size={{ xs: 12 }}
                            key={`client-note-${note.id}`}
                          >
                            <Box
                              p="1rem"
                              borderRadius="12px"
                              bgcolor={theme.palette.background.default}
                              boxShadow={theme.shadows[3]}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  mb: 0.75,
                                  gap: 1,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {note.CreatedOn
                                    ? `${dayjs(note.CreatedOn).format("M/D/YYYY h:mm A")}${
                                        note.Creator?.Name
                                          ? ` by ${note.Creator.Name}`
                                          : ""
                                      }`
                                    : note.Creator?.Name
                                      ? `by ${note.Creator.Name}`
                                      : ""}
                                </Typography>

                                <IconButton
                                  size="small"
                                  onClick={(event) =>
                                    handleClientNoteMenuOpen(event, note)
                                  }
                                  sx={{
                                    color: theme.palette.primary.main,
                                    width: 28,
                                    height: 28,
                                  }}
                                >
                                  <MoreVertRoundedIcon fontSize="small" />
                                </IconButton>
                              </Box>

                              <Typography
                                variant="body2"
                                sx={{ whiteSpace: "pre-wrap" }}
                              >
                                {note.Body}
                              </Typography>
                            </Box>
                          </Grid>
                        ))
                      ) : (
                        <Grid size={{ xs: 12 }}>
                          <Typography variant="body2" color="text.secondary">
                            No notes available.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>

                    <Menu
                      anchorEl={clientNoteAnchorEl}
                      open={Boolean(clientNoteAnchorEl)}
                      onClose={handleClientNoteMenuClose}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                    >
                      <MenuItem onClick={handleEditClientNote}>
                        <ListItemIcon>
                          <EditRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        <Typography variant="inherit">Edit</Typography>
                      </MenuItem>

                      <MenuItem onClick={handleDeleteClientNote}>
                        <ListItemIcon>
                          <DeleteRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        <Typography variant="inherit">Delete</Typography>
                      </MenuItem>
                    </Menu>

                    <Fab
                      color="primary"
                      aria-label="add-note"
                      sx={{
                        position: "absolute",
                        bottom: 10,
                        right: 10,
                        borderRadius: "12px",
                        width: 48,
                        height: 48,
                      }}
                      onClick={handleAddClientNote}
                    >
                      <AddRoundedIcon
                        sx={{
                          color: theme.palette.background.default,
                          fontSize: 24,
                        }}
                      />
                    </Fab>
                  </Box>
                </CustomTabPanel>

                <CustomTabPanel
                  value={isMobile ? 7 : tabValue}
                  index={7}
                  key="Tab Six"
                  ref={mobileSectionRefs[7]}
                  data-index={7}
                  stacked={isMobile}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    overflowY: "auto",
                  }}
                >
                  <History
                    serviceHistories={clientHistories}
                    entityType="client"
                  />
                </CustomTabPanel>
              </DragDropContext>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ClientProfile;
